// src/button/Button.tsx
import * as React2 from "react";
import { Slot } from "@radix-ui/react-slot";

// src/button/constants.ts
var baseStyles = [
  "inline-flex items-center justify-center",
  "font-mono font-extralight uppercase leading-[1.1] text-nowrap",
  "rounded-full",
  "transition-colors duration-200 ease-out",
  "hover:cursor-pointer",
  "focus:outline-none focus:ring-2 focus:ring-offset-2",
  "disabled:opacity-40 disabled:cursor-not-allowed"
].join(" ");
var variantStyles = {
  primary: "bg-[#00FBA9] text-[#171717] hover:bg-[#00e096] focus:ring-[#00FBA9]",
  secondary: "bg-transparent text-[#171717] dark:text-white border border-[#a9a9a9] hover:bg-white/5 focus:ring-white",
  danger: "bg-[#FF4C00] text-white hover:bg-[#E64400] focus:ring-[#FF4C00]",
  ghost: "bg-transparent text-[#171717] dark:text-white hover:opacity-70 focus:ring-0"
};
var sizeStyles = {
  sm: "h-8 px-4 text-[14px] tracking-wide",
  md: "h-11 px-6 text-[14px] tracking-normal lg:text-[18px] lg:tracking-[0.36px]",
  lg: "h-[52px] px-6 text-[18px] tracking-normal lg:text-[23px] lg:tracking-[0.46px]"
};
var iconOnlySizeStyles = {
  sm: "size-8",
  md: "size-11",
  lg: "size-[52px]"
};
var iconSpacing = "gap-2";
var iconOnlyVariantStyles = {
  primary: "bg-transparent text-[#171717] dark:text-white border border-[#a9a9a9] hover:bg-white/5 focus:ring-white",
  secondary: "bg-transparent text-[#171717] dark:text-white border border-[#a9a9a9] hover:bg-white/5 focus:ring-white",
  danger: "bg-transparent text-[#FF4C00] border border-[#a9a9a9] hover:bg-white/5 focus:ring-[#FF4C00]",
  ghost: "bg-transparent text-[#171717] dark:text-white hover:opacity-70 focus:ring-0"
};

// src/spinner/Spinner.tsx
import * as React from "react";

// src/spinner/constants.ts
var sizeStyles2 = {
  sm: "size-4",
  md: "size-6",
  lg: "size-8"
};
var svgDimensions = {
  sm: 16,
  md: 24,
  lg: 32
};

// src/spinner/Spinner.tsx
import { jsx, jsxs } from "react/jsx-runtime";
var Spinner = React.forwardRef(
  ({ size = "md", ...props }, ref) => {
    const dimension = svgDimensions[size];
    return /* @__PURE__ */ jsxs(
      "svg",
      {
        ref,
        className: `animate-spinner-orbit ${sizeStyles2[size]}`,
        width: dimension,
        height: dimension,
        viewBox: "0 0 32 32",
        fill: "none",
        xmlns: "http://www.w3.org/2000/svg",
        role: "status",
        "aria-label": "Loading",
        ...props,
        children: [
          /* @__PURE__ */ jsx(
            "path",
            {
              className: "animate-spinner-segment-1 origin-center",
              d: "M17.7773 12.9658L27.3135 3.42969L28.5703 4.68652L19.0342 14.2227H32V16H16V0H17.7773V12.9658Z",
              fill: "currentColor"
            }
          ),
          /* @__PURE__ */ jsx(
            "path",
            {
              className: "animate-spinner-segment-2 origin-center",
              d: "M14.2227 19.0342L4.68652 28.5703L3.42969 27.3135L12.9658 17.7773H0V16H16V32H14.2227V19.0342Z",
              fill: "currentColor"
            }
          ),
          /* @__PURE__ */ jsx(
            "path",
            {
              className: "animate-spinner-segment-3 origin-center",
              d: "M28.042 26.627L26.627 28.042L21.2627 22.6768L22.6768 21.2627L28.042 26.627Z",
              fill: "currentColor"
            }
          ),
          /* @__PURE__ */ jsx(
            "path",
            {
              className: "animate-spinner-segment-4 origin-center",
              d: "M11.3633 9.94922L9.94922 11.3633L4 5.41406L5.41406 4L11.3633 9.94922Z",
              fill: "currentColor"
            }
          )
        ]
      }
    );
  }
);
Spinner.displayName = "Spinner";

// src/button/Button.tsx
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var Button = React2.forwardRef(
  ({
    variant = "primary",
    size = "md",
    asChild = false,
    children,
    iconLeft,
    iconRight,
    loading = false,
    disabled,
    ...props
  }, ref) => {
    const Comp = asChild ? Slot : "button";
    const isIconOnly = (iconLeft || iconRight) && !children && !loading;
    const sizeStyle = isIconOnly ? iconOnlySizeStyles[size] : sizeStyles[size];
    const baseStylesClass = isIconOnly ? baseStyles.replace(/\buppercase\s*/g, "").trim() : baseStyles;
    const variantStyle = isIconOnly ? iconOnlyVariantStyles[variant] : variantStyles[variant];
    const hasLeftContent = loading || iconLeft;
    const combinedClassName = [
      baseStylesClass,
      variantStyle,
      sizeStyle,
      // Add icon spacing if has content on left/right
      !isIconOnly && (hasLeftContent || iconRight) ? iconSpacing : ""
    ].filter(Boolean).join(" ");
    const iconWrapperClass = "shrink-0 size-5 flex items-center justify-center";
    const isDisabled = loading || disabled;
    return /* @__PURE__ */ jsxs2(
      Comp,
      {
        ref,
        className: combinedClassName,
        disabled: isDisabled,
        "aria-busy": loading,
        ...props,
        children: [
          loading ? /* @__PURE__ */ jsx2("span", { className: iconWrapperClass, "aria-hidden": "true", children: /* @__PURE__ */ jsx2(Spinner, { size: "sm" }) }) : iconLeft ? /* @__PURE__ */ jsx2("span", { className: iconWrapperClass, "aria-hidden": "true", children: iconLeft }) : null,
          children && /* @__PURE__ */ jsx2("span", { children }),
          iconRight && !loading && /* @__PURE__ */ jsx2("span", { className: iconWrapperClass, "aria-hidden": "true", children: iconRight })
        ]
      }
    );
  }
);
Button.displayName = "Button";

// src/input/Input.tsx
import * as React3 from "react";

// src/input/constants.ts
var baseStyles2 = [
  "w-full",
  "font-sans font-light leading-[1.4]",
  "bg-transparent",
  "border",
  "transition-colors duration-200 ease-out",
  "placeholder:text-[#A9A9A9]",
  "focus:outline-none focus:ring-2 focus:ring-offset-0",
  "disabled:opacity-40 disabled:cursor-not-allowed"
].join(" ");
var variantStyles2 = {
  default: [
    "border-[#A9A9A9]",
    "text-[#171717] dark:text-white",
    "focus:border-[#171717] dark:focus:border-white",
    "focus:ring-[#171717]/20 dark:focus:ring-white/20"
  ].join(" "),
  error: [
    "border-[#FF4C00]",
    "text-[#171717] dark:text-white",
    "focus:border-[#FF4C00]",
    "focus:ring-[#FF4C00]/20"
  ].join(" ")
};
var sizeStyles3 = {
  md: "h-11 px-4 text-[14px] lg:text-[18px]",
  lg: "h-[52px] px-4 text-[18px] lg:text-[23px]"
};

// src/input/Input.tsx
import { jsx as jsx3 } from "react/jsx-runtime";
var Input = React3.forwardRef(
  ({
    variant = "default",
    size = "md",
    type = "text",
    ...props
  }, ref) => {
    const combinedClassName = [
      baseStyles2,
      variantStyles2[variant],
      sizeStyles3[size]
    ].join(" ");
    return /* @__PURE__ */ jsx3(
      "input",
      {
        ref,
        type,
        className: combinedClassName,
        ...props
      }
    );
  }
);
Input.displayName = "Input";

// src/label/Label.tsx
import * as React4 from "react";
import * as LabelPrimitive from "@radix-ui/react-label";

// src/label/constants.ts
var baseStyles3 = [
  "font-mono font-extralight uppercase tracking-wide leading-[1.1]",
  "text-[14px] lg:text-[18px]",
  "text-[#171717] dark:text-white",
  "cursor-default"
].join(" ");
var requiredStyles = "text-[#FF4C00] ml-1";

// src/label/Label.tsx
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
var Label = React4.forwardRef(({ required, children, ...props }, ref) => {
  return /* @__PURE__ */ jsxs3(
    LabelPrimitive.Root,
    {
      ref,
      className: baseStyles3,
      ...props,
      children: [
        children,
        required && /* @__PURE__ */ jsx4("span", { className: requiredStyles, "aria-hidden": "true", children: "*" })
      ]
    }
  );
});
Label.displayName = "Label";

// src/textarea/Textarea.tsx
import * as React5 from "react";

// src/textarea/constants.ts
var baseStyles4 = [
  "w-full min-h-[120px]",
  "font-sans font-light leading-[1.4]",
  "bg-transparent",
  "border",
  "resize-y",
  "transition-colors duration-200 ease-out",
  "placeholder:text-[#A9A9A9]",
  "focus:outline-none focus:ring-2 focus:ring-offset-0",
  "disabled:opacity-40 disabled:cursor-not-allowed disabled:resize-none"
].join(" ");
var variantStyles3 = {
  default: [
    "border-[#A9A9A9]",
    "text-[#171717] dark:text-white",
    "focus:border-[#171717] dark:focus:border-white",
    "focus:ring-[#171717]/20 dark:focus:ring-white/20"
  ].join(" "),
  error: [
    "border-[#FF4C00]",
    "text-[#171717] dark:text-white",
    "focus:border-[#FF4C00]",
    "focus:ring-[#FF4C00]/20"
  ].join(" ")
};
var sizeStyles4 = {
  md: "p-4 text-[14px] lg:text-[18px]",
  lg: "p-4 text-[18px] lg:text-[23px]"
};

// src/textarea/Textarea.tsx
import { jsx as jsx5 } from "react/jsx-runtime";
var Textarea = React5.forwardRef(
  ({
    variant = "default",
    size = "md",
    ...props
  }, ref) => {
    const combinedClassName = [
      baseStyles4,
      variantStyles3[variant],
      sizeStyles4[size]
    ].join(" ");
    return /* @__PURE__ */ jsx5(
      "textarea",
      {
        ref,
        className: combinedClassName,
        ...props
      }
    );
  }
);
Textarea.displayName = "Textarea";

// src/checkbox/Checkbox.tsx
import * as React6 from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";

// src/checkbox/constants.ts
var baseStyles5 = [
  "shrink-0",
  "border border-[#A9A9A9]",
  "bg-transparent",
  "transition-colors duration-200 ease-out",
  "hover:border-[#171717] dark:hover:border-white",
  "focus:outline-none focus:ring-2 focus:ring-[#3941EB] dark:focus:ring-[#00FBA9] focus:ring-offset-2",
  "disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-[#A9A9A9]",
  "data-[state=checked]:bg-[#3941EB] data-[state=checked]:border-[#3941EB]",
  "dark:data-[state=checked]:bg-[#00FBA9] dark:data-[state=checked]:border-[#00FBA9]",
  "data-[state=indeterminate]:bg-[#3941EB] data-[state=indeterminate]:border-[#3941EB]",
  "dark:data-[state=indeterminate]:bg-[#00FBA9] dark:data-[state=indeterminate]:border-[#00FBA9]"
].join(" ");
var sizeStyles5 = {
  md: "size-5",
  lg: "size-6"
};
var indicatorStyles = "flex items-center justify-center text-[#171717]";
var iconSizeStyles = {
  md: "size-3.5",
  lg: "size-4"
};

// src/checkbox/Checkbox.tsx
import { jsx as jsx6 } from "react/jsx-runtime";
var Checkbox = React6.forwardRef(({ size = "md", ...props }, ref) => {
  const combinedClassName = [baseStyles5, sizeStyles5[size]].join(" ");
  const iconSize = iconSizeStyles[size];
  return /* @__PURE__ */ jsx6(
    CheckboxPrimitive.Root,
    {
      ref,
      className: combinedClassName,
      ...props,
      children: /* @__PURE__ */ jsx6(CheckboxPrimitive.Indicator, { className: indicatorStyles, children: /* @__PURE__ */ jsx6(
        "svg",
        {
          className: iconSize,
          viewBox: "0 0 16 16",
          fill: "none",
          stroke: "currentColor",
          strokeWidth: "2",
          strokeLinecap: "square",
          strokeLinejoin: "miter",
          children: props.checked === "indeterminate" ? /* @__PURE__ */ jsx6("line", { x1: "3", y1: "8", x2: "13", y2: "8" }) : /* @__PURE__ */ jsx6("polyline", { points: "3 8 6 11 13 4" })
        }
      ) })
    }
  );
});
Checkbox.displayName = "Checkbox";

// src/radio/Radio.tsx
import * as React7 from "react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";

// src/radio/constants.ts
var groupStyles = "flex flex-col gap-3";
var itemBaseStyles = [
  "shrink-0 rounded-full",
  "border border-[#A9A9A9]",
  "bg-transparent",
  "transition-colors duration-200 ease-out",
  "hover:border-[#171717] dark:hover:border-white",
  "focus:outline-none focus:ring-2 focus:ring-[#3941EB] dark:focus:ring-[#00FBA9] focus:ring-offset-2",
  "disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-[#A9A9A9]",
  "data-[state=checked]:border-[#3941EB] dark:data-[state=checked]:border-[#00FBA9]"
].join(" ");
var itemSizeStyles = {
  md: "size-5",
  lg: "size-6"
};
var indicatorStyles2 = "flex items-center justify-center";
var dotStyles = "rounded-full bg-[#3941EB] dark:bg-[#00FBA9]";
var dotSizeStyles = {
  md: "size-2.5",
  lg: "size-3"
};

// src/radio/Radio.tsx
import { jsx as jsx7 } from "react/jsx-runtime";
var RadioSizeContext = React7.createContext("md");
var RadioGroup = React7.forwardRef(({ size = "md", ...props }, ref) => {
  return /* @__PURE__ */ jsx7(RadioSizeContext.Provider, { value: size, children: /* @__PURE__ */ jsx7(
    RadioGroupPrimitive.Root,
    {
      ref,
      className: groupStyles,
      ...props
    }
  ) });
});
RadioGroup.displayName = "RadioGroup";
var RadioItem = React7.forwardRef(({ size, ...props }, ref) => {
  const groupSize = React7.useContext(RadioSizeContext);
  const resolvedSize = size ?? groupSize;
  const itemClassName = [itemBaseStyles, itemSizeStyles[resolvedSize]].join(" ");
  const dotClassName = [dotStyles, dotSizeStyles[resolvedSize]].join(" ");
  return /* @__PURE__ */ jsx7(
    RadioGroupPrimitive.Item,
    {
      ref,
      className: itemClassName,
      ...props,
      children: /* @__PURE__ */ jsx7(RadioGroupPrimitive.Indicator, { className: indicatorStyles2, children: /* @__PURE__ */ jsx7("span", { className: dotClassName }) })
    }
  );
});
RadioItem.displayName = "RadioItem";

// src/switch/Switch.tsx
import * as React8 from "react";
import * as SwitchPrimitive from "@radix-ui/react-switch";

// src/switch/constants.ts
var rootBaseStyles = [
  "relative inline-flex shrink-0 cursor-pointer items-center rounded-full",
  "border border-transparent",
  "bg-[#A9A9A9]",
  "transition-colors duration-200 ease-out",
  "focus:outline-none focus:ring-2 focus:ring-[#3941EB] dark:focus:ring-[#00FBA9] focus:ring-offset-2",
  "disabled:opacity-40 disabled:cursor-not-allowed",
  "data-[state=checked]:bg-[#3941EB] dark:data-[state=checked]:bg-[#00FBA9]"
].join(" ");
var rootSizeStyles = {
  md: "h-6 w-11",
  lg: "h-7 w-[52px]"
};
var thumbBaseStyles = [
  "pointer-events-none block rounded-full bg-white",
  "transition-transform duration-200 ease-out"
].join(" ");
var thumbSizeStyles = {
  md: "size-5 data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0.5",
  lg: "size-6 data-[state=checked]:translate-x-6 data-[state=unchecked]:translate-x-0.5"
};

// src/switch/Switch.tsx
import { jsx as jsx8 } from "react/jsx-runtime";
var Switch = React8.forwardRef(({ size = "md", ...props }, ref) => {
  const rootClassName = [rootBaseStyles, rootSizeStyles[size]].join(" ");
  const thumbClassName = [thumbBaseStyles, thumbSizeStyles[size]].join(" ");
  return /* @__PURE__ */ jsx8(
    SwitchPrimitive.Root,
    {
      ref,
      className: rootClassName,
      ...props,
      children: /* @__PURE__ */ jsx8(SwitchPrimitive.Thumb, { className: thumbClassName })
    }
  );
});
Switch.displayName = "Switch";

// src/select/Select.tsx
import * as React9 from "react";
import * as SelectPrimitive from "@radix-ui/react-select";

// src/select/constants.ts
var triggerBaseStyles = [
  "inline-flex items-center justify-between w-full",
  "font-sans font-light leading-[1.4]",
  "bg-transparent",
  "border border-[#A9A9A9]",
  "text-[#171717] dark:text-white",
  "transition-colors duration-200 ease-out",
  "hover:border-[#171717] dark:hover:border-white",
  "focus:outline-none focus:ring-2 focus:ring-[#171717]/20 dark:focus:ring-white/20",
  "focus:border-[#171717] dark:focus:border-white",
  "disabled:opacity-40 disabled:cursor-not-allowed",
  "data-[placeholder]:text-[#A9A9A9]"
].join(" ");
var triggerSizeStyles = {
  md: "h-11 px-4 text-[14px] lg:text-[18px]",
  lg: "h-[52px] px-4 text-[18px] lg:text-[23px]"
};
var triggerIconStyles = "size-5 shrink-0 text-[#A9A9A9] transition-transform duration-200";
var contentStyles = [
  "relative z-50 max-h-96 min-w-[8rem] overflow-hidden",
  "bg-white dark:bg-[#171717]",
  "border border-[#A9A9A9]",
  // Animations - fade only, no bounce
  "data-[state=open]:animate-in data-[state=closed]:animate-out",
  "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
  "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
  "data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2"
].join(" ");
var viewportStyles = "p-1";
var itemStyles = [
  "relative flex w-full cursor-pointer select-none items-center py-3 px-4 pr-8",
  "font-sans font-light text-[14px] lg:text-[18px]",
  "text-[#171717] dark:text-white",
  "outline-none",
  "transition-colors duration-100",
  "hover:bg-[#FAFAFA] dark:hover:bg-white/5",
  "focus:bg-[#FAFAFA] dark:focus:bg-white/5",
  "data-[disabled]:opacity-40 data-[disabled]:pointer-events-none"
].join(" ");
var itemIndicatorStyles = "absolute right-3 flex items-center justify-center";
var checkIconStyles = "size-4 text-[#3941EB] dark:text-[#00FBA9]";
var labelStyles = [
  "px-4 py-2",
  "font-mono font-extralight text-[14px] lg:text-[18px] uppercase tracking-wide",
  "text-[#A9A9A9]"
].join(" ");
var separatorStyles = "h-px my-1 bg-[#A9A9A9]/20";
var scrollButtonStyles = [
  "flex items-center justify-center h-6 cursor-default",
  "text-[#A9A9A9]"
].join(" ");

// src/select/Select.tsx
import { jsx as jsx9, jsxs as jsxs4 } from "react/jsx-runtime";
var SelectSizeContext = React9.createContext("md");
var Select = SelectPrimitive.Root;
var SelectValue = SelectPrimitive.Value;
var SelectTrigger = React9.forwardRef(({ size = "md", children, ...props }, ref) => {
  const triggerClassName = [triggerBaseStyles, triggerSizeStyles[size]].join(" ");
  return /* @__PURE__ */ jsx9(SelectSizeContext.Provider, { value: size, children: /* @__PURE__ */ jsxs4(
    SelectPrimitive.Trigger,
    {
      ref,
      className: triggerClassName,
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx9(SelectPrimitive.Icon, { asChild: true, children: /* @__PURE__ */ jsx9(
          "svg",
          {
            className: triggerIconStyles,
            viewBox: "0 0 24 24",
            fill: "none",
            stroke: "currentColor",
            strokeWidth: "1.5",
            children: /* @__PURE__ */ jsx9("path", { d: "M6 9l6 6 6-6" })
          }
        ) })
      ]
    }
  ) });
});
SelectTrigger.displayName = "SelectTrigger";
var SelectScrollUpButton = React9.forwardRef((props, ref) => /* @__PURE__ */ jsx9(
  SelectPrimitive.ScrollUpButton,
  {
    ref,
    className: scrollButtonStyles,
    ...props,
    children: /* @__PURE__ */ jsx9("svg", { className: "size-4", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.5", children: /* @__PURE__ */ jsx9("path", { d: "M18 15l-6-6-6 6" }) })
  }
));
SelectScrollUpButton.displayName = "SelectScrollUpButton";
var SelectScrollDownButton = React9.forwardRef((props, ref) => /* @__PURE__ */ jsx9(
  SelectPrimitive.ScrollDownButton,
  {
    ref,
    className: scrollButtonStyles,
    ...props,
    children: /* @__PURE__ */ jsx9("svg", { className: "size-4", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.5", children: /* @__PURE__ */ jsx9("path", { d: "M6 9l6 6 6-6" }) })
  }
));
SelectScrollDownButton.displayName = "SelectScrollDownButton";
var SelectContent = React9.forwardRef(({ children, position = "popper", ...props }, ref) => /* @__PURE__ */ jsx9(SelectPrimitive.Portal, { children: /* @__PURE__ */ jsxs4(
  SelectPrimitive.Content,
  {
    ref,
    className: contentStyles,
    position,
    sideOffset: 4,
    ...props,
    children: [
      /* @__PURE__ */ jsx9(SelectScrollUpButton, {}),
      /* @__PURE__ */ jsx9(SelectPrimitive.Viewport, { className: viewportStyles, children }),
      /* @__PURE__ */ jsx9(SelectScrollDownButton, {})
    ]
  }
) }));
SelectContent.displayName = "SelectContent";
var SelectItem = React9.forwardRef(({ children, ...props }, ref) => /* @__PURE__ */ jsxs4(
  SelectPrimitive.Item,
  {
    ref,
    className: itemStyles,
    ...props,
    children: [
      /* @__PURE__ */ jsx9(SelectPrimitive.ItemText, { children }),
      /* @__PURE__ */ jsx9(SelectPrimitive.ItemIndicator, { className: itemIndicatorStyles, children: /* @__PURE__ */ jsx9("svg", { className: checkIconStyles, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: /* @__PURE__ */ jsx9("polyline", { points: "20 6 9 17 4 12" }) }) })
    ]
  }
));
SelectItem.displayName = "SelectItem";
var SelectGroup = React9.forwardRef((props, ref) => /* @__PURE__ */ jsx9(SelectPrimitive.Group, { ref, ...props }));
SelectGroup.displayName = "SelectGroup";
var SelectLabel = React9.forwardRef((props, ref) => /* @__PURE__ */ jsx9(
  SelectPrimitive.Label,
  {
    ref,
    className: labelStyles,
    ...props
  }
));
SelectLabel.displayName = "SelectLabel";
var SelectSeparator = React9.forwardRef((props, ref) => /* @__PURE__ */ jsx9(
  SelectPrimitive.Separator,
  {
    ref,
    className: separatorStyles,
    ...props
  }
));
SelectSeparator.displayName = "SelectSeparator";

// src/badge/Badge.tsx
import * as React10 from "react";

// src/badge/constants.ts
var baseStyles6 = [
  "inline-flex items-center justify-center",
  "font-mono font-extralight uppercase tracking-wide leading-[1.1]",
  "rounded-full"
].join(" ");
var variantStyles4 = {
  default: "bg-[#171717] text-white dark:bg-[#FAFAFA] dark:text-[#171717]",
  success: "bg-[#3941EB]/15 text-[#3941EB] dark:bg-[#00FBA9]/20 dark:text-[#00FBA9]",
  warning: "bg-[#A9A9A9]/20 text-[#171717] dark:bg-[#A9A9A9]/20 dark:text-white",
  error: "bg-[#FF4C00]/15 text-[#FF4C00] dark:bg-[#FF4C00]/20 dark:text-[#FFB788]"
};
var sizeStyles6 = {
  sm: "h-6 px-2 text-[14px]",
  md: "h-7 px-3 text-[14px] lg:text-[18px]"
};
var iconWrapperStyles = "shrink-0 size-3";
var iconGapStyles = "gap-1";

// src/badge/Badge.tsx
import { jsx as jsx10, jsxs as jsxs5 } from "react/jsx-runtime";
var Badge = React10.forwardRef(
  ({
    variant = "default",
    size = "md",
    icon,
    children,
    ...props
  }, ref) => {
    const combinedClassName = [
      baseStyles6,
      variantStyles4[variant],
      sizeStyles6[size],
      icon ? iconGapStyles : ""
    ].filter(Boolean).join(" ");
    return /* @__PURE__ */ jsxs5(
      "span",
      {
        ref,
        className: combinedClassName,
        ...props,
        children: [
          icon && /* @__PURE__ */ jsx10("span", { className: iconWrapperStyles, "aria-hidden": "true", children: icon }),
          children
        ]
      }
    );
  }
);
Badge.displayName = "Badge";

// src/avatar/Avatar.tsx
import * as React11 from "react";
import * as AvatarPrimitive from "@radix-ui/react-avatar";

// src/avatar/constants.ts
var rootBaseStyles2 = [
  "relative flex shrink-0 overflow-hidden rounded-full",
  "bg-[#FAFAFA] dark:bg-[#171717]"
].join(" ");
var rootSizeStyles2 = {
  sm: "size-8",
  md: "size-10",
  lg: "size-12",
  xl: "size-16"
};
var imageStyles = "aspect-square size-full object-cover";
var fallbackBaseStyles = [
  "flex size-full items-center justify-center",
  "font-mono font-extralight uppercase",
  "bg-[#A9A9A9]/20",
  "text-[#171717] dark:text-white"
].join(" ");
var fallbackSizeStyles = {
  sm: "text-[14px]",
  md: "text-[14px]",
  lg: "text-[14px]",
  xl: "text-[18px]"
};

// src/avatar/Avatar.tsx
import { jsx as jsx11 } from "react/jsx-runtime";
var AvatarSizeContext = React11.createContext("md");
var Avatar = React11.forwardRef(({ size = "md", ...props }, ref) => {
  const rootClassName = [rootBaseStyles2, rootSizeStyles2[size]].join(" ");
  return /* @__PURE__ */ jsx11(AvatarSizeContext.Provider, { value: size, children: /* @__PURE__ */ jsx11(
    AvatarPrimitive.Root,
    {
      ref,
      className: rootClassName,
      ...props
    }
  ) });
});
Avatar.displayName = "Avatar";
var AvatarImage = React11.forwardRef((props, ref) => /* @__PURE__ */ jsx11(
  AvatarPrimitive.Image,
  {
    ref,
    className: imageStyles,
    ...props
  }
));
AvatarImage.displayName = "AvatarImage";
var AvatarFallback = React11.forwardRef((props, ref) => {
  const size = React11.useContext(AvatarSizeContext);
  const fallbackClassName = [fallbackBaseStyles, fallbackSizeStyles[size]].join(" ");
  return /* @__PURE__ */ jsx11(
    AvatarPrimitive.Fallback,
    {
      ref,
      className: fallbackClassName,
      ...props
    }
  );
});
AvatarFallback.displayName = "AvatarFallback";

// src/dialog/Dialog.tsx
import * as React12 from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";

// node_modules/@solar-icons/react/dist/esm/defs/arrows/AltArrowDown.mjs
import e from "react";
var r = /* @__PURE__ */ new Map([
  ["Bold", /* @__PURE__ */ e.createElement(e.Fragment, null, /* @__PURE__ */ e.createElement("path", { d: "M12.3704 15.8351L18.8001 9.20467C19.2013 8.79094 18.9581 8 18.4297 8H5.5703C5.04189 8 4.79869 8.79094 5.1999 9.20467L11.6296 15.8351C11.8427 16.0549 12.1573 16.0549 12.3704 15.8351Z", fill: "currentColor" }))],
  ["BoldDuotone", /* @__PURE__ */ e.createElement(e.Fragment, null, /* @__PURE__ */ e.createElement("path", { d: "M8.30273 12.4044L11.6296 15.8351C11.8428 16.0549 12.1573 16.0549 12.3704 15.8351L18.8001 9.20467C19.2013 8.79094 18.9581 8 18.4297 8H12.7071L8.30273 12.4044Z", fill: "currentColor" }), /* @__PURE__ */ e.createElement("path", { opacity: "0.5", d: "M11.2929 8H5.5703C5.04189 8 4.79869 8.79094 5.1999 9.20467L7.60648 11.6864L11.2929 8Z", fill: "currentColor" }))],
  ["Broken", /* @__PURE__ */ e.createElement(e.Fragment, null, /* @__PURE__ */ e.createElement("path", { d: "M19 9L12 15L10.25 13.5M5 9L7.33333 11", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }))],
  ["Linear", /* @__PURE__ */ e.createElement(e.Fragment, null, /* @__PURE__ */ e.createElement("path", { d: "M19 9L12 15L5 9", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }))],
  ["LineDuotone", /* @__PURE__ */ e.createElement(e.Fragment, null, /* @__PURE__ */ e.createElement("path", { d: "M19 9L12 15L5 9", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }))],
  ["Outline", /* @__PURE__ */ e.createElement(e.Fragment, null, /* @__PURE__ */ e.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M4.43057 8.51192C4.70014 8.19743 5.17361 8.161 5.48811 8.43057L12 14.0122L18.5119 8.43057C18.8264 8.16101 19.2999 8.19743 19.5695 8.51192C19.839 8.82642 19.8026 9.29989 19.4881 9.56946L12.4881 15.5695C12.2072 15.8102 11.7928 15.8102 11.5119 15.5695L4.51192 9.56946C4.19743 9.29989 4.161 8.82641 4.43057 8.51192Z", fill: "currentColor" }))]
]);

// node_modules/@solar-icons/react/dist/esm/defs/arrows/AltArrowLeft.mjs
import e2 from "react";
var r2 = /* @__PURE__ */ new Map([
  ["Bold", /* @__PURE__ */ e2.createElement(e2.Fragment, null, /* @__PURE__ */ e2.createElement("path", { d: "M8.16485 11.6296L14.7953 5.1999C15.2091 4.79869 16 5.04189 16 5.5703L16 18.4297C16 18.9581 15.2091 19.2013 14.7953 18.8001L8.16485 12.3704C7.94505 12.1573 7.94505 11.8427 8.16485 11.6296Z", fill: "currentColor" }))],
  ["BoldDuotone", /* @__PURE__ */ e2.createElement(e2.Fragment, null, /* @__PURE__ */ e2.createElement("path", { d: "M11.5956 8.30273L8.16485 11.6296C7.94505 11.8428 7.94505 12.1573 8.16485 12.3704L14.7953 18.8001C15.2091 19.2013 16 18.9581 16 18.4297V12.7071L11.5956 8.30273Z", fill: "currentColor" }), /* @__PURE__ */ e2.createElement("path", { opacity: "0.5", d: "M15.9999 11.2929L15.9999 5.5703C15.9999 5.04189 15.2089 4.79869 14.7952 5.1999L12.3135 7.60648L15.9999 11.2929Z", fill: "currentColor" }))],
  ["Broken", /* @__PURE__ */ e2.createElement(e2.Fragment, null, /* @__PURE__ */ e2.createElement("path", { d: "M15 19L9 12L10.5 10.25M15 5L13 7.33333", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }))],
  ["Linear", /* @__PURE__ */ e2.createElement(e2.Fragment, null, /* @__PURE__ */ e2.createElement("path", { d: "M15 5L9 12L15 19", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }))],
  ["LineDuotone", /* @__PURE__ */ e2.createElement(e2.Fragment, null, /* @__PURE__ */ e2.createElement("path", { d: "M15 5L9 12L15 19", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }))],
  ["Outline", /* @__PURE__ */ e2.createElement(e2.Fragment, null, /* @__PURE__ */ e2.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M15.4881 4.43057C15.8026 4.70014 15.839 5.17361 15.5694 5.48811L9.98781 12L15.5694 18.5119C15.839 18.8264 15.8026 19.2999 15.4881 19.5695C15.1736 19.839 14.7001 19.8026 14.4306 19.4881L8.43056 12.4881C8.18981 12.2072 8.18981 11.7928 8.43056 11.5119L14.4306 4.51192C14.7001 4.19743 15.1736 4.161 15.4881 4.43057Z", fill: "currentColor" }))]
]);

// node_modules/@solar-icons/react/dist/esm/defs/arrows/AltArrowRight.mjs
import e3 from "react";
var r3 = /* @__PURE__ */ new Map([
  ["Bold", /* @__PURE__ */ e3.createElement(e3.Fragment, null, /* @__PURE__ */ e3.createElement("path", { d: "M15.8351 11.6296L9.20467 5.1999C8.79094 4.79869 8 5.04189 8 5.5703L8 18.4297C8 18.9581 8.79094 19.2013 9.20467 18.8001L15.8351 12.3704C16.055 12.1573 16.0549 11.8427 15.8351 11.6296Z", fill: "currentColor" }))],
  ["BoldDuotone", /* @__PURE__ */ e3.createElement(e3.Fragment, null, /* @__PURE__ */ e3.createElement("path", { d: "M12.4044 8.30273L15.8351 11.6296C16.0549 11.8428 16.0549 12.1573 15.8351 12.3704L9.20467 18.8001C8.79094 19.2013 8 18.9581 8 18.4297V12.7071L12.4044 8.30273Z", fill: "currentColor" }), /* @__PURE__ */ e3.createElement("path", { opacity: "0.5", d: "M8 11.2929L8 5.5703C8 5.04189 8.79094 4.79869 9.20467 5.1999L11.6864 7.60648L8 11.2929Z", fill: "currentColor" }))],
  ["Broken", /* @__PURE__ */ e3.createElement(e3.Fragment, null, /* @__PURE__ */ e3.createElement("path", { d: "M9 5L11 7.33333M9 19L15 12L13.5 10.25", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }))],
  ["Linear", /* @__PURE__ */ e3.createElement(e3.Fragment, null, /* @__PURE__ */ e3.createElement("path", { d: "M9 5L15 12L9 19", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }))],
  ["LineDuotone", /* @__PURE__ */ e3.createElement(e3.Fragment, null, /* @__PURE__ */ e3.createElement("path", { d: "M9 5L15 12L9 19", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }))],
  ["Outline", /* @__PURE__ */ e3.createElement(e3.Fragment, null, /* @__PURE__ */ e3.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M8.51192 4.43057C8.82641 4.161 9.29989 4.19743 9.56946 4.51192L15.5695 11.5119C15.8102 11.7928 15.8102 12.2072 15.5695 12.4881L9.56946 19.4881C9.29989 19.8026 8.82641 19.839 8.51192 19.5695C8.19743 19.2999 8.161 18.8264 8.43057 18.5119L14.0122 12L8.43057 5.48811C8.161 5.17361 8.19743 4.70014 8.51192 4.43057Z", fill: "currentColor" }))]
]);

// node_modules/@solar-icons/react/dist/esm/defs/search/Magnifer.mjs
import e4 from "react";
var r4 = /* @__PURE__ */ new Map([
  ["Bold", /* @__PURE__ */ e4.createElement(e4.Fragment, null, /* @__PURE__ */ e4.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M21.7883 21.7883C22.0706 21.506 22.0706 21.0483 21.7883 20.7659L18.1224 17.1002C19.4884 15.5007 20.3133 13.425 20.3133 11.1566C20.3133 6.09956 16.2137 2 11.1566 2C6.09956 2 2 6.09956 2 11.1566C2 16.2137 6.09956 20.3133 11.1566 20.3133C13.4249 20.3133 15.5006 19.4885 17.1 18.1225L20.7659 21.7883C21.0483 22.0706 21.506 22.0706 21.7883 21.7883Z", fill: "currentColor" }))],
  ["BoldDuotone", /* @__PURE__ */ e4.createElement(e4.Fragment, null, /* @__PURE__ */ e4.createElement("path", { opacity: "0.5", d: "M20.3133 11.1566C20.3133 16.2137 16.2137 20.3133 11.1566 20.3133C6.09956 20.3133 2 16.2137 2 11.1566C2 6.09956 6.09956 2 11.1566 2C16.2137 2 20.3133 6.09956 20.3133 11.1566Z", fill: "currentColor" }), /* @__PURE__ */ e4.createElement("path", { d: "M17.1001 18.1219L20.7664 21.7882C21.0487 22.0705 21.5064 22.0705 21.7887 21.7882C22.071 21.5059 22.071 21.0482 21.7887 20.7659L18.1224 17.0996C17.809 17.4666 17.4671 17.8085 17.1001 18.1219Z", fill: "currentColor" }))],
  ["Broken", /* @__PURE__ */ e4.createElement(e4.Fragment, null, /* @__PURE__ */ e4.createElement("path", { d: "M18.5 18.5L22 22", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round" }), /* @__PURE__ */ e4.createElement("path", { d: "M6.75 3.27093C8.14732 2.46262 9.76964 2 11.5 2C16.7467 2 21 6.25329 21 11.5C21 16.7467 16.7467 21 11.5 21C6.25329 21 2 16.7467 2 11.5C2 9.76964 2.46262 8.14732 3.27093 6.75", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round" }))],
  ["Linear", /* @__PURE__ */ e4.createElement(e4.Fragment, null, /* @__PURE__ */ e4.createElement("circle", { cx: "11.5", cy: "11.5", r: "9.5", stroke: "currentColor", strokeWidth: "1.5" }), /* @__PURE__ */ e4.createElement("path", { d: "M18.5 18.5L22 22", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round" }))],
  ["LineDuotone", /* @__PURE__ */ e4.createElement(e4.Fragment, null, /* @__PURE__ */ e4.createElement("circle", { cx: "11.5", cy: "11.5", r: "9.5", stroke: "currentColor", strokeWidth: "1.5" }), /* @__PURE__ */ e4.createElement("path", { d: "M18.5 18.5L22 22", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round" }))],
  ["Outline", /* @__PURE__ */ e4.createElement(e4.Fragment, null, /* @__PURE__ */ e4.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M11.5 2.75C6.66751 2.75 2.75 6.66751 2.75 11.5C2.75 16.3325 6.66751 20.25 11.5 20.25C16.3325 20.25 20.25 16.3325 20.25 11.5C20.25 6.66751 16.3325 2.75 11.5 2.75ZM1.25 11.5C1.25 5.83908 5.83908 1.25 11.5 1.25C17.1609 1.25 21.75 5.83908 21.75 11.5C21.75 14.0605 20.8111 16.4017 19.2589 18.1982L22.5303 21.4697C22.8232 21.7626 22.8232 22.2374 22.5303 22.5303C22.2374 22.8232 21.7626 22.8232 21.4697 22.5303L18.1982 19.2589C16.4017 20.8111 14.0605 21.75 11.5 21.75C5.83908 21.75 1.25 17.1609 1.25 11.5Z", fill: "currentColor" }))]
]);

// node_modules/@solar-icons/react/dist/esm/defs/ui/CheckCircle.mjs
import e5 from "react";
var r5 = /* @__PURE__ */ new Map([
  ["Bold", /* @__PURE__ */ e5.createElement(e5.Fragment, null, /* @__PURE__ */ e5.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12ZM16.0303 8.96967C16.3232 9.26256 16.3232 9.73744 16.0303 10.0303L11.0303 15.0303C10.7374 15.3232 10.2626 15.3232 9.96967 15.0303L7.96967 13.0303C7.67678 12.7374 7.67678 12.2626 7.96967 11.9697C8.26256 11.6768 8.73744 11.6768 9.03033 11.9697L10.5 13.4393L12.7348 11.2045L14.9697 8.96967C15.2626 8.67678 15.7374 8.67678 16.0303 8.96967Z", fill: "currentColor" }))],
  ["BoldDuotone", /* @__PURE__ */ e5.createElement(e5.Fragment, null, /* @__PURE__ */ e5.createElement("path", { opacity: "0.5", d: "M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z", fill: "currentColor" }), /* @__PURE__ */ e5.createElement("path", { d: "M16.0303 8.96967C16.3232 9.26256 16.3232 9.73744 16.0303 10.0303L11.0303 15.0303C10.7374 15.3232 10.2626 15.3232 9.96967 15.0303L7.96967 13.0303C7.67678 12.7374 7.67678 12.2626 7.96967 11.9697C8.26256 11.6768 8.73744 11.6768 9.03033 11.9697L10.5 13.4393L12.7348 11.2045L14.9697 8.96967C15.2626 8.67678 15.7374 8.67678 16.0303 8.96967Z", fill: "currentColor" }))],
  ["Broken", /* @__PURE__ */ e5.createElement(e5.Fragment, null, /* @__PURE__ */ e5.createElement("path", { d: "M8.5 12.5L10.5 14.5L15.5 9.5", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }), /* @__PURE__ */ e5.createElement("path", { d: "M7 3.33782C8.47087 2.48697 10.1786 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 10.1786 2.48697 8.47087 3.33782 7", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round" }))],
  ["Linear", /* @__PURE__ */ e5.createElement(e5.Fragment, null, /* @__PURE__ */ e5.createElement("circle", { cx: "12", cy: "12", r: "10", stroke: "currentColor", strokeWidth: "1.5" }), /* @__PURE__ */ e5.createElement("path", { d: "M8.5 12.5L10.5 14.5L15.5 9.5", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }))],
  ["LineDuotone", /* @__PURE__ */ e5.createElement(e5.Fragment, null, /* @__PURE__ */ e5.createElement("circle", { opacity: "0.5", cx: "12", cy: "12", r: "10", stroke: "currentColor", strokeWidth: "1.5" }), /* @__PURE__ */ e5.createElement("path", { d: "M8.5 12.5L10.5 14.5L15.5 9.5", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }))],
  ["Outline", /* @__PURE__ */ e5.createElement(e5.Fragment, null, /* @__PURE__ */ e5.createElement("path", { d: "M16.0303 10.0303C16.3232 9.73744 16.3232 9.26256 16.0303 8.96967C15.7374 8.67678 15.2626 8.67678 14.9697 8.96967L10.5 13.4393L9.03033 11.9697C8.73744 11.6768 8.26256 11.6768 7.96967 11.9697C7.67678 12.2626 7.67678 12.7374 7.96967 13.0303L9.96967 15.0303C10.2626 15.3232 10.7374 15.3232 11.0303 15.0303L16.0303 10.0303Z", fill: "currentColor" }), /* @__PURE__ */ e5.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M12 1.25C6.06294 1.25 1.25 6.06294 1.25 12C1.25 17.9371 6.06294 22.75 12 22.75C17.9371 22.75 22.75 17.9371 22.75 12C22.75 6.06294 17.9371 1.25 12 1.25ZM2.75 12C2.75 6.89137 6.89137 2.75 12 2.75C17.1086 2.75 21.25 6.89137 21.25 12C21.25 17.1086 17.1086 21.25 12 21.25C6.89137 21.25 2.75 17.1086 2.75 12Z", fill: "currentColor" }))]
]);

// node_modules/@solar-icons/react/dist/esm/defs/ui/CloseCircle.mjs
import e6 from "react";
var r6 = /* @__PURE__ */ new Map([
  ["Bold", /* @__PURE__ */ e6.createElement(e6.Fragment, null, /* @__PURE__ */ e6.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12ZM8.96963 8.96965C9.26252 8.67676 9.73739 8.67676 10.0303 8.96965L12 10.9393L13.9696 8.96967C14.2625 8.67678 14.7374 8.67678 15.0303 8.96967C15.3232 9.26256 15.3232 9.73744 15.0303 10.0303L13.0606 12L15.0303 13.9696C15.3232 14.2625 15.3232 14.7374 15.0303 15.0303C14.7374 15.3232 14.2625 15.3232 13.9696 15.0303L12 13.0607L10.0303 15.0303C9.73742 15.3232 9.26254 15.3232 8.96965 15.0303C8.67676 14.7374 8.67676 14.2625 8.96965 13.9697L10.9393 12L8.96963 10.0303C8.67673 9.73742 8.67673 9.26254 8.96963 8.96965Z", fill: "currentColor" }))],
  ["BoldDuotone", /* @__PURE__ */ e6.createElement(e6.Fragment, null, /* @__PURE__ */ e6.createElement("path", { opacity: "0.5", d: "M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z", fill: "currentColor" }), /* @__PURE__ */ e6.createElement("path", { d: "M8.96967 8.96967C9.26256 8.67678 9.73744 8.67678 10.0303 8.96967L12 10.9394L13.9697 8.96969C14.2626 8.6768 14.7374 8.6768 15.0303 8.96969C15.3232 9.26258 15.3232 9.73746 15.0303 10.0304L13.0607 12L15.0303 13.9696C15.3232 14.2625 15.3232 14.7374 15.0303 15.0303C14.7374 15.3232 14.2625 15.3232 13.9696 15.0303L12 13.0607L10.0304 15.0303C9.73746 15.3232 9.26258 15.3232 8.96969 15.0303C8.6768 14.7374 8.6768 14.2626 8.96969 13.9697L10.9394 12L8.96967 10.0303C8.67678 9.73744 8.67678 9.26256 8.96967 8.96967Z", fill: "currentColor" }))],
  ["Broken", /* @__PURE__ */ e6.createElement(e6.Fragment, null, /* @__PURE__ */ e6.createElement("path", { d: "M14.5 9.50002L9.5 14.5M9.49998 9.5L14.5 14.5", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round" }), /* @__PURE__ */ e6.createElement("path", { d: "M7 3.33782C8.47087 2.48697 10.1786 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 10.1786 2.48697 8.47087 3.33782 7", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round" }))],
  ["Linear", /* @__PURE__ */ e6.createElement(e6.Fragment, null, /* @__PURE__ */ e6.createElement("circle", { cx: "12", cy: "12", r: "10", stroke: "currentColor", strokeWidth: "1.5" }), /* @__PURE__ */ e6.createElement("path", { d: "M14.5 9.50002L9.5 14.5M9.49998 9.5L14.5 14.5", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round" }))],
  ["LineDuotone", /* @__PURE__ */ e6.createElement(e6.Fragment, null, /* @__PURE__ */ e6.createElement("circle", { opacity: "0.5", cx: "12", cy: "12", r: "10", stroke: "currentColor", strokeWidth: "1.5" }), /* @__PURE__ */ e6.createElement("path", { d: "M14.5 9.50002L9.5 14.5M9.49998 9.5L14.5 14.5", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round" }))],
  ["Outline", /* @__PURE__ */ e6.createElement(e6.Fragment, null, /* @__PURE__ */ e6.createElement("path", { d: "M10.0303 8.96965C9.73741 8.67676 9.26253 8.67676 8.96964 8.96965C8.67675 9.26255 8.67675 9.73742 8.96964 10.0303L10.9393 12L8.96966 13.9697C8.67677 14.2625 8.67677 14.7374 8.96966 15.0303C9.26255 15.3232 9.73743 15.3232 10.0303 15.0303L12 13.0607L13.9696 15.0303C14.2625 15.3232 14.7374 15.3232 15.0303 15.0303C15.3232 14.7374 15.3232 14.2625 15.0303 13.9696L13.0606 12L15.0303 10.0303C15.3232 9.73744 15.3232 9.26257 15.0303 8.96968C14.7374 8.67678 14.2625 8.67678 13.9696 8.96968L12 10.9393L10.0303 8.96965Z", fill: "currentColor" }), /* @__PURE__ */ e6.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M12 1.25C6.06294 1.25 1.25 6.06294 1.25 12C1.25 17.9371 6.06294 22.75 12 22.75C17.9371 22.75 22.75 17.9371 22.75 12C22.75 6.06294 17.9371 1.25 12 1.25ZM2.75 12C2.75 6.89137 6.89137 2.75 12 2.75C17.1086 2.75 21.25 6.89137 21.25 12C21.25 17.1086 17.1086 21.25 12 21.25C6.89137 21.25 2.75 17.1086 2.75 12Z", fill: "currentColor" }))]
]);

// node_modules/@solar-icons/react/dist/esm/defs/ui/DangerTriangle.mjs
import e7 from "react";
var r7 = /* @__PURE__ */ new Map([
  ["Bold", /* @__PURE__ */ e7.createElement(e7.Fragment, null, /* @__PURE__ */ e7.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M5.31171 10.7615C8.23007 5.58716 9.68925 3 12 3C14.3107 3 15.7699 5.58716 18.6883 10.7615L19.0519 11.4063C21.4771 15.7061 22.6897 17.856 21.5937 19.428C20.4978 21 17.7864 21 12.3637 21H11.6363C6.21356 21 3.50217 21 2.40626 19.428C1.31034 17.856 2.52291 15.7061 4.94805 11.4063L5.31171 10.7615ZM12 7.25C12.4142 7.25 12.75 7.58579 12.75 8V13C12.75 13.4142 12.4142 13.75 12 13.75C11.5858 13.75 11.25 13.4142 11.25 13V8C11.25 7.58579 11.5858 7.25 12 7.25ZM12 17C12.5523 17 13 16.5523 13 16C13 15.4477 12.5523 15 12 15C11.4477 15 11 15.4477 11 16C11 16.5523 11.4477 17 12 17Z", fill: "currentColor" }))],
  ["BoldDuotone", /* @__PURE__ */ e7.createElement(e7.Fragment, null, /* @__PURE__ */ e7.createElement("path", { opacity: "0.5", d: "M12 3C9.68925 3 8.23007 5.58716 5.31171 10.7615L4.94805 11.4063C2.52291 15.7061 1.31034 17.856 2.40626 19.428C3.50217 21 6.21356 21 11.6363 21H12.3637C17.7864 21 20.4978 21 21.5937 19.428C22.6897 17.856 21.4771 15.7061 19.0519 11.4063L18.6883 10.7615C15.7699 5.58716 14.3107 3 12 3Z", fill: "currentColor" }), /* @__PURE__ */ e7.createElement("path", { d: "M12 7.25C12.4142 7.25 12.75 7.58579 12.75 8V13C12.75 13.4142 12.4142 13.75 12 13.75C11.5858 13.75 11.25 13.4142 11.25 13V8C11.25 7.58579 11.5858 7.25 12 7.25Z", fill: "currentColor" }), /* @__PURE__ */ e7.createElement("path", { d: "M12 17C12.5523 17 13 16.5523 13 16C13 15.4477 12.5523 15 12 15C11.4477 15 11 15.4477 11 16C11 16.5523 11.4477 17 12 17Z", fill: "currentColor" }))],
  ["Broken", /* @__PURE__ */ e7.createElement(e7.Fragment, null, /* @__PURE__ */ e7.createElement("path", { d: "M6.30928 9C8.59494 5 9.96832 3 12 3C14.3107 3 15.7699 5.58716 18.6883 10.7615L19.0519 11.4063C21.4771 15.7061 22.6897 17.856 21.5937 19.428C20.4978 21 17.7864 21 12.3637 21H11.6363C6.21356 21 3.50217 21 2.40626 19.428C1.45498 18.0635 2.24306 16.2635 4.05373 13", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round" }), /* @__PURE__ */ e7.createElement("path", { d: "M12 8V13", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round" }), /* @__PURE__ */ e7.createElement("circle", { cx: "12", cy: "16", r: "1", fill: "currentColor" }))],
  ["Linear", /* @__PURE__ */ e7.createElement(e7.Fragment, null, /* @__PURE__ */ e7.createElement("path", { d: "M5.31171 10.7615C8.23007 5.58716 9.68925 3 12 3C14.3107 3 15.7699 5.58716 18.6883 10.7615L19.0519 11.4063C21.4771 15.7061 22.6897 17.856 21.5937 19.428C20.4978 21 17.7864 21 12.3637 21H11.6363C6.21356 21 3.50217 21 2.40626 19.428C1.31034 17.856 2.52291 15.7061 4.94805 11.4063L5.31171 10.7615Z", stroke: "currentColor", strokeWidth: "1.5" }), /* @__PURE__ */ e7.createElement("path", { d: "M12 8V13", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round" }), /* @__PURE__ */ e7.createElement("circle", { cx: "12", cy: "16", r: "1", fill: "currentColor" }))],
  ["LineDuotone", /* @__PURE__ */ e7.createElement(e7.Fragment, null, /* @__PURE__ */ e7.createElement("path", { opacity: "0.5", d: "M5.31171 10.7615C8.23007 5.58716 9.68925 3 12 3C14.3107 3 15.7699 5.58716 18.6883 10.7615L19.0519 11.4063C21.4771 15.7061 22.6897 17.856 21.5937 19.428C20.4978 21 17.7864 21 12.3637 21H11.6363C6.21356 21 3.50217 21 2.40626 19.428C1.31034 17.856 2.52291 15.7061 4.94805 11.4063L5.31171 10.7615Z", stroke: "currentColor", strokeWidth: "1.5" }), /* @__PURE__ */ e7.createElement("path", { d: "M12 8V13", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round" }), /* @__PURE__ */ e7.createElement("circle", { cx: "12", cy: "16", r: "1", fill: "currentColor" }))],
  ["Outline", /* @__PURE__ */ e7.createElement(e7.Fragment, null, /* @__PURE__ */ e7.createElement("path", { d: "M12 7.25C12.4142 7.25 12.75 7.58579 12.75 8V13C12.75 13.4142 12.4142 13.75 12 13.75C11.5858 13.75 11.25 13.4142 11.25 13V8C11.25 7.58579 11.5858 7.25 12 7.25Z", fill: "currentColor" }), /* @__PURE__ */ e7.createElement("path", { d: "M12 17C12.5523 17 13 16.5523 13 16C13 15.4477 12.5523 15 12 15C11.4477 15 11 15.4477 11 16C11 16.5523 11.4477 17 12 17Z", fill: "currentColor" }), /* @__PURE__ */ e7.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M8.2944 4.47643C9.36631 3.11493 10.5018 2.25 12 2.25C13.4981 2.25 14.6336 3.11493 15.7056 4.47643C16.7598 5.81544 17.8769 7.79622 19.3063 10.3305L19.7418 11.1027C20.9234 13.1976 21.8566 14.8523 22.3468 16.1804C22.8478 17.5376 22.9668 18.7699 22.209 19.8569C21.4736 20.9118 20.2466 21.3434 18.6991 21.5471C17.1576 21.75 15.0845 21.75 12.4248 21.75H11.5752C8.91552 21.75 6.84239 21.75 5.30082 21.5471C3.75331 21.3434 2.52637 20.9118 1.79099 19.8569C1.03318 18.7699 1.15218 17.5376 1.65314 16.1804C2.14334 14.8523 3.07658 13.1977 4.25818 11.1027L4.69361 10.3307C6.123 7.79629 7.24019 5.81547 8.2944 4.47643ZM9.47297 5.40432C8.49896 6.64148 7.43704 8.51988 5.96495 11.1299L5.60129 11.7747C4.37507 13.9488 3.50368 15.4986 3.06034 16.6998C2.6227 17.8855 2.68338 18.5141 3.02148 18.9991C3.38202 19.5163 4.05873 19.8706 5.49659 20.0599C6.92858 20.2484 8.9026 20.25 11.6363 20.25H12.3636C15.0974 20.25 17.0714 20.2484 18.5034 20.0599C19.9412 19.8706 20.6179 19.5163 20.9785 18.9991C21.3166 18.5141 21.3773 17.8855 20.9396 16.6998C20.4963 15.4986 19.6249 13.9488 18.3987 11.7747L18.035 11.1299C16.5629 8.51987 15.501 6.64148 14.527 5.40431C13.562 4.17865 12.8126 3.75 12 3.75C11.1874 3.75 10.4379 4.17865 9.47297 5.40432Z", fill: "currentColor" }))]
]);

// node_modules/@solar-icons/react/dist/esm/defs/ui/InfoCircle.mjs
import e8 from "react";
var t = /* @__PURE__ */ new Map([
  ["Bold", /* @__PURE__ */ e8.createElement(e8.Fragment, null, /* @__PURE__ */ e8.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12ZM12 17.75C12.4142 17.75 12.75 17.4142 12.75 17V11C12.75 10.5858 12.4142 10.25 12 10.25C11.5858 10.25 11.25 10.5858 11.25 11V17C11.25 17.4142 11.5858 17.75 12 17.75ZM12 7C12.5523 7 13 7.44772 13 8C13 8.55228 12.5523 9 12 9C11.4477 9 11 8.55228 11 8C11 7.44772 11.4477 7 12 7Z", fill: "currentColor" }))],
  ["BoldDuotone", /* @__PURE__ */ e8.createElement(e8.Fragment, null, /* @__PURE__ */ e8.createElement("path", { opacity: "0.5", d: "M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z", fill: "currentColor" }), /* @__PURE__ */ e8.createElement("path", { d: "M12 17.75C12.4142 17.75 12.75 17.4142 12.75 17V11C12.75 10.5858 12.4142 10.25 12 10.25C11.5858 10.25 11.25 10.5858 11.25 11V17C11.25 17.4142 11.5858 17.75 12 17.75Z", fill: "currentColor" }), /* @__PURE__ */ e8.createElement("path", { d: "M12 7C12.5523 7 13 7.44771 13 8C13 8.55229 12.5523 9 12 9C11.4477 9 11 8.55229 11 8C11 7.44771 11.4477 7 12 7Z", fill: "currentColor" }))],
  ["Broken", /* @__PURE__ */ e8.createElement(e8.Fragment, null, /* @__PURE__ */ e8.createElement("path", { d: "M12 17V11", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round" }), /* @__PURE__ */ e8.createElement("circle", { cx: "1", cy: "1", r: "1", transform: "matrix(1 0 0 -1 11 9)", fill: "currentColor" }), /* @__PURE__ */ e8.createElement("path", { d: "M7 3.33782C8.47087 2.48697 10.1786 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 10.1786 2.48697 8.47087 3.33782 7", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round" }))],
  ["Linear", /* @__PURE__ */ e8.createElement(e8.Fragment, null, /* @__PURE__ */ e8.createElement("circle", { cx: "12", cy: "12", r: "10", stroke: "currentColor", strokeWidth: "1.5" }), /* @__PURE__ */ e8.createElement("path", { d: "M12 17V11", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round" }), /* @__PURE__ */ e8.createElement("circle", { cx: "1", cy: "1", r: "1", transform: "matrix(1 0 0 -1 11 9)", fill: "currentColor" }))],
  ["LineDuotone", /* @__PURE__ */ e8.createElement(e8.Fragment, null, /* @__PURE__ */ e8.createElement("circle", { opacity: "0.5", cx: "12", cy: "12", r: "10", stroke: "currentColor", strokeWidth: "1.5" }), /* @__PURE__ */ e8.createElement("path", { d: "M12 17V11", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round" }), /* @__PURE__ */ e8.createElement("circle", { cx: "1", cy: "1", r: "1", transform: "matrix(1 0 0 -1 11 9)", fill: "currentColor" }))],
  ["Outline", /* @__PURE__ */ e8.createElement(e8.Fragment, null, /* @__PURE__ */ e8.createElement("path", { d: "M12 17.75C12.4142 17.75 12.75 17.4142 12.75 17V11C12.75 10.5858 12.4142 10.25 12 10.25C11.5858 10.25 11.25 10.5858 11.25 11V17C11.25 17.4142 11.5858 17.75 12 17.75Z", fill: "currentColor" }), /* @__PURE__ */ e8.createElement("path", { d: "M12 7C12.5523 7 13 7.44772 13 8C13 8.55228 12.5523 9 12 9C11.4477 9 11 8.55228 11 8C11 7.44772 11.4477 7 12 7Z", fill: "currentColor" }), /* @__PURE__ */ e8.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M1.25 12C1.25 6.06294 6.06294 1.25 12 1.25C17.9371 1.25 22.75 6.06294 22.75 12C22.75 17.9371 17.9371 22.75 12 22.75C6.06294 22.75 1.25 17.9371 1.25 12ZM12 2.75C6.89137 2.75 2.75 6.89137 2.75 12C2.75 17.1086 6.89137 21.25 12 21.25C17.1086 21.25 21.25 17.1086 21.25 12C21.25 6.89137 17.1086 2.75 12 2.75Z", fill: "currentColor" }))]
]);

// node_modules/@solar-icons/react/dist/esm/defs/ui/MenuDots.mjs
import e9 from "react";
var t2 = /* @__PURE__ */ new Map([
  ["Bold", /* @__PURE__ */ e9.createElement(e9.Fragment, null, /* @__PURE__ */ e9.createElement("path", { d: "M7 12C7 13.1046 6.10457 14 5 14C3.89543 14 3 13.1046 3 12C3 10.8954 3.89543 10 5 10C6.10457 10 7 10.8954 7 12Z", fill: "currentColor" }), /* @__PURE__ */ e9.createElement("path", { d: "M14 12C14 13.1046 13.1046 14 12 14C10.8954 14 10 13.1046 10 12C10 10.8954 10.8954 10 12 10C13.1046 10 14 10.8954 14 12Z", fill: "currentColor" }), /* @__PURE__ */ e9.createElement("path", { d: "M21 12C21 13.1046 20.1046 14 19 14C17.8954 14 17 13.1046 17 12C17 10.8954 17.8954 10 19 10C20.1046 10 21 10.8954 21 12Z", fill: "currentColor" }))],
  ["BoldDuotone", /* @__PURE__ */ e9.createElement(e9.Fragment, null, /* @__PURE__ */ e9.createElement("path", { d: "M7 12C7 13.1046 6.10457 14 5 14C3.89543 14 3 13.1046 3 12C3 10.8954 3.89543 10 5 10C6.10457 10 7 10.8954 7 12Z", fill: "currentColor" }), /* @__PURE__ */ e9.createElement("path", { d: "M21 12C21 13.1046 20.1046 14 19 14C17.8954 14 17 13.1046 17 12C17 10.8954 17.8954 10 19 10C20.1046 10 21 10.8954 21 12Z", fill: "currentColor" }), /* @__PURE__ */ e9.createElement("path", { opacity: "0.5", d: "M14 12C14 13.1046 13.1046 14 12 14C10.8954 14 10 13.1046 10 12C10 10.8954 10.8954 10 12 10C13.1046 10 14 10.8954 14 12Z", fill: "currentColor" }))],
  ["Broken", /* @__PURE__ */ e9.createElement(e9.Fragment, null, /* @__PURE__ */ e9.createElement("path", { d: "M5 14C6.10457 14 7 13.1046 7 12C7 10.8954 6.10457 10 5 10C3.89543 10 3 10.8954 3 12", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round" }), /* @__PURE__ */ e9.createElement("circle", { cx: "12", cy: "12", r: "2", stroke: "currentColor", strokeWidth: "1.5" }), /* @__PURE__ */ e9.createElement("path", { d: "M21 12C21 13.1046 20.1046 14 19 14C17.8954 14 17 13.1046 17 12C17 10.8954 17.8954 10 19 10", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round" }))],
  ["Linear", /* @__PURE__ */ e9.createElement(e9.Fragment, null, /* @__PURE__ */ e9.createElement("circle", { cx: "5", cy: "12", r: "2", stroke: "currentColor", strokeWidth: "1.5" }), /* @__PURE__ */ e9.createElement("circle", { cx: "12", cy: "12", r: "2", stroke: "currentColor", strokeWidth: "1.5" }), /* @__PURE__ */ e9.createElement("circle", { cx: "19", cy: "12", r: "2", stroke: "currentColor", strokeWidth: "1.5" }))],
  ["LineDuotone", /* @__PURE__ */ e9.createElement(e9.Fragment, null, /* @__PURE__ */ e9.createElement("circle", { cx: "5", cy: "12", r: "2", stroke: "currentColor", strokeWidth: "1.5" }), /* @__PURE__ */ e9.createElement("circle", { opacity: "0.5", cx: "12", cy: "12", r: "2", stroke: "currentColor", strokeWidth: "1.5" }), /* @__PURE__ */ e9.createElement("circle", { cx: "19", cy: "12", r: "2", stroke: "currentColor", strokeWidth: "1.5" }))],
  ["Outline", /* @__PURE__ */ e9.createElement(e9.Fragment, null, /* @__PURE__ */ e9.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M2.25 12C2.25 10.4812 3.48122 9.25 5 9.25C6.51878 9.25 7.75 10.4812 7.75 12C7.75 13.5188 6.51878 14.75 5 14.75C3.48122 14.75 2.25 13.5188 2.25 12ZM5 10.75C4.30964 10.75 3.75 11.3096 3.75 12C3.75 12.6904 4.30964 13.25 5 13.25C5.69036 13.25 6.25 12.6904 6.25 12C6.25 11.3096 5.69036 10.75 5 10.75Z", fill: "currentColor" }), /* @__PURE__ */ e9.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M9.25 12C9.25 10.4812 10.4812 9.25 12 9.25C13.5188 9.25 14.75 10.4812 14.75 12C14.75 13.5188 13.5188 14.75 12 14.75C10.4812 14.75 9.25 13.5188 9.25 12ZM12 10.75C11.3096 10.75 10.75 11.3096 10.75 12C10.75 12.6904 11.3096 13.25 12 13.25C12.6904 13.25 13.25 12.6904 13.25 12C13.25 11.3096 12.6904 10.75 12 10.75Z", fill: "currentColor" }), /* @__PURE__ */ e9.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M19 9.25C17.4812 9.25 16.25 10.4812 16.25 12C16.25 13.5188 17.4812 14.75 19 14.75C20.5188 14.75 21.75 13.5188 21.75 12C21.75 10.4812 20.5188 9.25 19 9.25ZM17.75 12C17.75 11.3096 18.3096 10.75 19 10.75C19.6904 10.75 20.25 11.3096 20.25 12C20.25 12.6904 19.6904 13.25 19 13.25C18.3096 13.25 17.75 12.6904 17.75 12Z", fill: "currentColor" }))]
]);

// node_modules/@solar-icons/react/dist/esm/csr/arrows/AltArrowDown.mjs
import s2, { forwardRef as c2 } from "react";

// node_modules/@solar-icons/react/dist/esm/lib/IconBase.mjs
import w, { forwardRef as P } from "react";

// node_modules/@solar-icons/react/dist/esm/lib/context.mjs
import C, { createContext as f, useState as c, useContext as h } from "react";
var l = {
  color: "currentColor",
  size: "1em",
  weight: "Linear",
  mirrored: false
};
var S = f({
  value: l,
  setValue: () => {
  },
  setSvgProps: () => {
  }
});
var L = () => h(S);

// node_modules/@solar-icons/react/dist/esm/lib/IconBase.mjs
var I = Object.defineProperty;
var l2 = Object.getOwnPropertySymbols;
var d = Object.prototype.hasOwnProperty;
var f2 = Object.prototype.propertyIsEnumerable;
var m = (e12, t8, r9) => t8 in e12 ? I(e12, t8, { enumerable: true, configurable: true, writable: true, value: r9 }) : e12[t8] = r9;
var s = (e12, t8) => {
  for (var r9 in t8 || (t8 = {}))
    d.call(t8, r9) && m(e12, r9, t8[r9]);
  if (l2)
    for (var r9 of l2(t8))
      f2.call(t8, r9) && m(e12, r9, t8[r9]);
  return e12;
};
var g = (e12, t8) => {
  var r9 = {};
  for (var o4 in e12)
    d.call(e12, o4) && t8.indexOf(o4) < 0 && (r9[o4] = e12[o4]);
  if (e12 != null && l2)
    for (var o4 of l2(e12))
      t8.indexOf(o4) < 0 && f2.call(e12, o4) && (r9[o4] = e12[o4]);
  return r9;
};
var S2 = P((e12, t8) => {
  const i10 = e12, { alt: r9, color: o4, size: n6, weight: a8, mirrored: h2, children: p10, weights: x } = i10, u = g(i10, ["alt", "color", "size", "weight", "mirrored", "children", "weights"]), {
    value: {
      color: v = "currentColor",
      size: c11,
      weight: B = "Linear",
      mirrored: C5 = false
    },
    svgProps: E
  } = L();
  return /* @__PURE__ */ w.createElement(
    "svg",
    s(s({
      ref: t8,
      xmlns: "http://www.w3.org/2000/svg",
      width: n6 != null ? n6 : c11,
      height: n6 != null ? n6 : c11,
      color: o4 != null ? o4 : v,
      fill: "none",
      viewBox: "0 0 24 24",
      transform: h2 || C5 ? "scale(-1, 1)" : void 0
    }, E), u),
    !!r9 && /* @__PURE__ */ w.createElement("title", null, r9),
    p10,
    x.get(a8 != null ? a8 : B)
  );
});
S2.displayName = "IconBase";

// node_modules/@solar-icons/react/dist/esm/csr/arrows/AltArrowDown.mjs
var w2 = Object.defineProperty;
var i = Object.defineProperties;
var l3 = Object.getOwnPropertyDescriptors;
var e10 = Object.getOwnPropertySymbols;
var n = Object.prototype.hasOwnProperty;
var p = Object.prototype.propertyIsEnumerable;
var a = (r9, o4, t8) => o4 in r9 ? w2(r9, o4, { enumerable: true, configurable: true, writable: true, value: t8 }) : r9[o4] = t8;
var m2 = (r9, o4) => {
  for (var t8 in o4 || (o4 = {}))
    n.call(o4, t8) && a(r9, t8, o4[t8]);
  if (e10)
    for (var t8 of e10(o4))
      p.call(o4, t8) && a(r9, t8, o4[t8]);
  return r9;
};
var f3 = (r9, o4) => i(r9, l3(o4));
var D = c2((r9, o4) => /* @__PURE__ */ s2.createElement(S2, f3(m2({ ref: o4 }, r9), { weights: r })));
D.displayName = "AltArrowDown";

// node_modules/@solar-icons/react/dist/esm/csr/arrows/AltArrowLeft.mjs
import w3, { forwardRef as A } from "react";
var i2 = Object.defineProperty;
var l4 = Object.defineProperties;
var p2 = Object.getOwnPropertyDescriptors;
var o = Object.getOwnPropertySymbols;
var s3 = Object.prototype.hasOwnProperty;
var c3 = Object.prototype.propertyIsEnumerable;
var a2 = (t8, r9, e12) => r9 in t8 ? i2(t8, r9, { enumerable: true, configurable: true, writable: true, value: e12 }) : t8[r9] = e12;
var f4 = (t8, r9) => {
  for (var e12 in r9 || (r9 = {}))
    s3.call(r9, e12) && a2(t8, e12, r9[e12]);
  if (o)
    for (var e12 of o(r9))
      c3.call(r9, e12) && a2(t8, e12, r9[e12]);
  return t8;
};
var m3 = (t8, r9) => l4(t8, p2(r9));
var L2 = A((t8, r9) => /* @__PURE__ */ w3.createElement(S2, m3(f4({ ref: r9 }, t8), { weights: r2 })));
L2.displayName = "AltArrowLeft";

// node_modules/@solar-icons/react/dist/esm/csr/arrows/AltArrowRight.mjs
import w4, { forwardRef as A2 } from "react";
var f5 = Object.defineProperty;
var l5 = Object.defineProperties;
var p3 = Object.getOwnPropertyDescriptors;
var e11 = Object.getOwnPropertySymbols;
var s4 = Object.prototype.hasOwnProperty;
var c4 = Object.prototype.propertyIsEnumerable;
var a3 = (t8, r9, o4) => r9 in t8 ? f5(t8, r9, { enumerable: true, configurable: true, writable: true, value: o4 }) : t8[r9] = o4;
var m4 = (t8, r9) => {
  for (var o4 in r9 || (r9 = {}))
    s4.call(r9, o4) && a3(t8, o4, r9[o4]);
  if (e11)
    for (var o4 of e11(r9))
      c4.call(r9, o4) && a3(t8, o4, r9[o4]);
  return t8;
};
var i3 = (t8, r9) => l5(t8, p3(r9));
var g2 = A2((t8, r9) => /* @__PURE__ */ w4.createElement(S2, i3(m4({ ref: r9 }, t8), { weights: r3 })));
g2.displayName = "AltArrowRight";

// node_modules/@solar-icons/react/dist/esm/csr/search/Magnifer.mjs
import d2, { forwardRef as g3 } from "react";
var i4 = Object.defineProperty;
var n2 = Object.defineProperties;
var p4 = Object.getOwnPropertyDescriptors;
var o2 = Object.getOwnPropertySymbols;
var s5 = Object.prototype.hasOwnProperty;
var c5 = Object.prototype.propertyIsEnumerable;
var t3 = (r9, e12, a8) => e12 in r9 ? i4(r9, e12, { enumerable: true, configurable: true, writable: true, value: a8 }) : r9[e12] = a8;
var f6 = (r9, e12) => {
  for (var a8 in e12 || (e12 = {}))
    s5.call(e12, a8) && t3(r9, a8, e12[a8]);
  if (o2)
    for (var a8 of o2(e12))
      c5.call(e12, a8) && t3(r9, a8, e12[a8]);
  return r9;
};
var m5 = (r9, e12) => n2(r9, p4(e12));
var M = g3((r9, e12) => /* @__PURE__ */ d2.createElement(S2, m5(f6({ ref: e12 }, r9), { weights: r4 })));
M.displayName = "Magnifer";

// node_modules/@solar-icons/react/dist/esm/csr/ui/CheckCircle.mjs
import C2, { forwardRef as d3 } from "react";
var i5 = Object.defineProperty;
var f7 = Object.defineProperties;
var l6 = Object.getOwnPropertyDescriptors;
var t4 = Object.getOwnPropertySymbols;
var p5 = Object.prototype.hasOwnProperty;
var s6 = Object.prototype.propertyIsEnumerable;
var a4 = (r9, e12, o4) => e12 in r9 ? i5(r9, e12, { enumerable: true, configurable: true, writable: true, value: o4 }) : r9[e12] = o4;
var c6 = (r9, e12) => {
  for (var o4 in e12 || (e12 = {}))
    p5.call(e12, o4) && a4(r9, o4, e12[o4]);
  if (t4)
    for (var o4 of t4(e12))
      s6.call(e12, o4) && a4(r9, o4, e12[o4]);
  return r9;
};
var m6 = (r9, e12) => f7(r9, l6(e12));
var k = d3((r9, e12) => /* @__PURE__ */ C2.createElement(S2, m6(c6({ ref: e12 }, r9), { weights: r5 })));
k.displayName = "CheckCircle";

// node_modules/@solar-icons/react/dist/esm/csr/ui/CloseCircle.mjs
import C3, { forwardRef as d4 } from "react";
var l7 = Object.defineProperty;
var s7 = Object.defineProperties;
var c7 = Object.getOwnPropertyDescriptors;
var t5 = Object.getOwnPropertySymbols;
var f8 = Object.prototype.hasOwnProperty;
var p6 = Object.prototype.propertyIsEnumerable;
var a5 = (o4, e12, r9) => e12 in o4 ? l7(o4, e12, { enumerable: true, configurable: true, writable: true, value: r9 }) : o4[e12] = r9;
var m7 = (o4, e12) => {
  for (var r9 in e12 || (e12 = {}))
    f8.call(e12, r9) && a5(o4, r9, e12[r9]);
  if (t5)
    for (var r9 of t5(e12))
      p6.call(e12, r9) && a5(o4, r9, e12[r9]);
  return o4;
};
var i6 = (o4, e12) => s7(o4, c7(e12));
var R = d4((o4, e12) => /* @__PURE__ */ C3.createElement(S2, i6(m7({ ref: e12 }, o4), { weights: r6 })));
R.displayName = "CloseCircle";

// node_modules/@solar-icons/react/dist/esm/csr/ui/DangerTriangle.mjs
import s8, { forwardRef as c8 } from "react";
var n3 = Object.defineProperty;
var f9 = Object.defineProperties;
var g4 = Object.getOwnPropertyDescriptors;
var o3 = Object.getOwnPropertySymbols;
var l8 = Object.prototype.hasOwnProperty;
var p7 = Object.prototype.propertyIsEnumerable;
var t6 = (r9, e12, a8) => e12 in r9 ? n3(r9, e12, { enumerable: true, configurable: true, writable: true, value: a8 }) : r9[e12] = a8;
var m8 = (r9, e12) => {
  for (var a8 in e12 || (e12 = {}))
    l8.call(e12, a8) && t6(r9, a8, e12[a8]);
  if (o3)
    for (var a8 of o3(e12))
      p7.call(e12, a8) && t6(r9, a8, e12[a8]);
  return r9;
};
var i7 = (r9, e12) => f9(r9, g4(e12));
var D2 = c8((r9, e12) => /* @__PURE__ */ s8.createElement(S2, i7(m8({ ref: e12 }, r9), { weights: r7 })));
D2.displayName = "DangerTriangle";

// node_modules/@solar-icons/react/dist/esm/csr/ui/InfoCircle.mjs
import s9, { forwardRef as d5 } from "react";
var i8 = Object.defineProperty;
var c9 = Object.defineProperties;
var l9 = Object.getOwnPropertyDescriptors;
var t7 = Object.getOwnPropertySymbols;
var n4 = Object.prototype.hasOwnProperty;
var p8 = Object.prototype.propertyIsEnumerable;
var a6 = (o4, e12, r9) => e12 in o4 ? i8(o4, e12, { enumerable: true, configurable: true, writable: true, value: r9 }) : o4[e12] = r9;
var f10 = (o4, e12) => {
  for (var r9 in e12 || (e12 = {}))
    n4.call(e12, r9) && a6(o4, r9, e12[r9]);
  if (t7)
    for (var r9 of t7(e12))
      p8.call(e12, r9) && a6(o4, r9, e12[r9]);
  return o4;
};
var m9 = (o4, e12) => c9(o4, l9(e12));
var C4 = d5((o4, e12) => /* @__PURE__ */ s9.createElement(S2, m9(f10({ ref: e12 }, o4), { weights: t })));
C4.displayName = "InfoCircle";

// node_modules/@solar-icons/react/dist/esm/csr/ui/MenuDots.mjs
import d6, { forwardRef as l10 } from "react";
var f11 = Object.defineProperty;
var i9 = Object.defineProperties;
var n5 = Object.getOwnPropertyDescriptors;
var r8 = Object.getOwnPropertySymbols;
var p9 = Object.prototype.hasOwnProperty;
var c10 = Object.prototype.propertyIsEnumerable;
var a7 = (o4, e12, t8) => e12 in o4 ? f11(o4, e12, { enumerable: true, configurable: true, writable: true, value: t8 }) : o4[e12] = t8;
var m10 = (o4, e12) => {
  for (var t8 in e12 || (e12 = {}))
    p9.call(e12, t8) && a7(o4, t8, e12[t8]);
  if (r8)
    for (var t8 of r8(e12))
      c10.call(e12, t8) && a7(o4, t8, e12[t8]);
  return o4;
};
var s10 = (o4, e12) => i9(o4, n5(e12));
var D3 = l10((o4, e12) => /* @__PURE__ */ d6.createElement(S2, s10(m10({ ref: e12 }, o4), { weights: t2 })));
D3.displayName = "MenuDots";

// src/dialog/constants.ts
var overlayStyles = [
  "fixed inset-0 z-50",
  "bg-black/60 backdrop-blur-sm",
  "hidden lg:block",
  "data-[state=open]:animate-in data-[state=closed]:animate-out",
  "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
].join(" ");
var contentBaseStyles = [
  "fixed z-50",
  "inset-0",
  "lg:inset-auto lg:left-1/2 lg:top-1/2",
  "lg:-translate-x-1/2 lg:-translate-y-1/2",
  "bg-white dark:bg-[#171717]",
  "text-[#171717] dark:text-white",
  "p-6 lg:p-8",
  // Flex column to anchor footer at bottom on mobile
  "flex flex-col",
  "lg:data-[state=open]:animate-in lg:data-[state=closed]:animate-out",
  "lg:data-[state=closed]:fade-out-0 lg:data-[state=open]:fade-in-0",
  "lg:data-[state=closed]:zoom-out-95 lg:data-[state=open]:zoom-in-95",
  "lg:data-[state=closed]:slide-out-to-left-1/2 lg:data-[state=closed]:slide-out-to-top-[48%]",
  "lg:data-[state=open]:slide-in-from-left-1/2 lg:data-[state=open]:slide-in-from-top-[48%]",
  "lg:duration-200",
  "focus:outline-none",
  "overflow-y-auto"
].join(" ");
var contentSizeStyles = {
  sm: "lg:w-full lg:max-w-[400px] lg:h-auto",
  md: "lg:w-full lg:max-w-[500px] lg:h-auto",
  lg: "lg:w-full lg:max-w-[600px] lg:h-auto",
  xl: "lg:w-full lg:max-w-[800px] lg:h-auto",
  full: "lg:w-[calc(100%-64px)] lg:h-[calc(100%-64px)] lg:max-w-none"
};
var headerStyles = "flex flex-col gap-2 mb-6";
var footerStyles = "flex flex-col-reverse gap-3 mt-auto pt-8 [&>*]:w-full lg:mt-8 lg:pt-0 lg:flex-row lg:justify-end lg:[&>*]:w-auto";
var titleStyles = [
  "font-sans font-light",
  "text-[23px] lg:text-[29px]",
  "leading-[1.1]",
  "text-[#171717] dark:text-white"
].join(" ");
var descriptionStyles = [
  "font-sans font-light",
  "text-[14px] lg:text-[18px]",
  "text-[#A9A9A9]"
].join(" ");
var closeButtonStyles = [
  "absolute right-6 top-6 lg:right-8 lg:top-8",
  "flex items-center justify-center",
  "text-[#A9A9A9]",
  "hover:text-[#171717] dark:hover:text-white",
  "transition-colors duration-200",
  "focus:outline-none"
].join(" ");
var bodyTextStyles = [
  "font-sans font-light",
  "text-[14px] lg:text-[18px]",
  "text-[#171717] dark:text-white"
].join(" ");
var secondaryTextStyles = [
  "font-sans font-light",
  "text-[14px] lg:text-[18px]",
  "text-[#A9A9A9]"
].join(" ");
var subheadingStyles = [
  "font-sans font-light",
  "text-[18px] lg:text-[23px]",
  "leading-[1.1]",
  "text-[#171717] dark:text-white"
].join(" ");

// src/dialog/Dialog.tsx
import { jsx as jsx12, jsxs as jsxs6 } from "react/jsx-runtime";
var Dialog = DialogPrimitive.Root;
Dialog.displayName = "Dialog";
var DialogTrigger = DialogPrimitive.Trigger;
DialogTrigger.displayName = "DialogTrigger";
var DialogPortal = DialogPrimitive.Portal;
DialogPortal.displayName = "DialogPortal";
var DialogClose = DialogPrimitive.Close;
DialogClose.displayName = "DialogClose";
var DialogOverlay = React12.forwardRef(({ ...props }, ref) => /* @__PURE__ */ jsx12(
  DialogPrimitive.Overlay,
  {
    ref,
    className: overlayStyles,
    ...props
  }
));
DialogOverlay.displayName = "DialogOverlay";
var DialogContent = React12.forwardRef(({ size = "md", children, ...props }, ref) => /* @__PURE__ */ jsxs6(DialogPortal, { children: [
  /* @__PURE__ */ jsx12(DialogOverlay, {}),
  /* @__PURE__ */ jsxs6(
    DialogPrimitive.Content,
    {
      ref,
      className: `${contentBaseStyles} ${contentSizeStyles[size]}`,
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsxs6(DialogPrimitive.Close, { className: closeButtonStyles, children: [
          /* @__PURE__ */ jsx12(R, { className: "size-6 stroke-current", strokeWidth: 1 }),
          /* @__PURE__ */ jsx12("span", { className: "sr-only", children: "Close" })
        ] })
      ]
    }
  )
] }));
DialogContent.displayName = "DialogContent";
var DialogHeader = ({ ...props }) => /* @__PURE__ */ jsx12("div", { className: headerStyles, ...props });
DialogHeader.displayName = "DialogHeader";
var DialogFooter = ({ ...props }) => /* @__PURE__ */ jsx12("div", { className: footerStyles, ...props });
DialogFooter.displayName = "DialogFooter";
var DialogTitle = React12.forwardRef(({ ...props }, ref) => /* @__PURE__ */ jsx12(
  DialogPrimitive.Title,
  {
    ref,
    className: titleStyles,
    ...props
  }
));
DialogTitle.displayName = "DialogTitle";
var DialogDescription = React12.forwardRef(({ ...props }, ref) => /* @__PURE__ */ jsx12(
  DialogPrimitive.Description,
  {
    ref,
    className: descriptionStyles,
    ...props
  }
));
DialogDescription.displayName = "DialogDescription";

// src/toast/Toast.tsx
import * as React13 from "react";
import * as ToastPrimitive from "@radix-ui/react-toast";

// src/toast/constants.ts
var viewportBaseStyles = [
  "fixed z-[100]",
  "p-6",
  "w-full max-w-[420px]",
  "outline-none",
  "flex flex-col-reverse gap-2"
].join(" ");
var viewportPlacementStyles = {
  "top-left": `${viewportBaseStyles} top-0 left-0 flex-col`,
  "top-center": `${viewportBaseStyles} top-0 left-1/2 -translate-x-1/2 flex-col`,
  "top-right": `${viewportBaseStyles} top-0 right-0 flex-col`,
  "bottom-left": `${viewportBaseStyles} bottom-0 left-0`,
  "bottom-center": `${viewportBaseStyles} bottom-0 left-1/2 -translate-x-1/2`,
  "bottom-right": `${viewportBaseStyles} bottom-0 right-0`
};
var viewportStyles2 = viewportPlacementStyles["bottom-right"];
var toastBaseStyles = [
  "group pointer-events-auto",
  "relative flex items-center gap-3",
  "w-full px-4 py-3",
  "bg-white dark:bg-[#171717]",
  "border border-[#E5E5E5] dark:border-[#333333]",
  "text-[#171717] dark:text-white",
  // Swipe to dismiss
  "data-[swipe=cancel]:translate-x-0",
  "data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)]",
  "data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)]",
  "data-[swipe=move]:transition-none",
  // Enter animation - ease-out only per brand
  "data-[state=open]:animate-in",
  "data-[state=open]:fade-in-0",
  "data-[state=open]:slide-in-from-bottom-4",
  "data-[state=open]:ease-out",
  "data-[state=open]:duration-200",
  // Exit animation
  "data-[state=closed]:animate-out",
  "data-[state=closed]:fade-out-0",
  "data-[state=closed]:ease-out",
  "data-[state=closed]:duration-200"
].join(" ");
var iconStyles = {
  default: "text-[#171717] dark:text-white",
  success: "text-[#3941EB] dark:text-[#00FBA9]",
  warning: "text-[#171717] dark:text-white",
  error: "text-[#FF4C00]"
};
var titleStyles2 = [
  "font-sans font-light",
  "text-[14px]",
  "leading-[1.3]",
  "text-[#171717] dark:text-white"
].join(" ");
var descriptionStyles2 = [
  "font-sans font-light",
  "text-[14px]",
  "text-[#A9A9A9]"
].join(" ");

// src/toast/Toast.tsx
import { jsx as jsx13, jsxs as jsxs7 } from "react/jsx-runtime";
var ToastProvider = ToastPrimitive.Provider;
ToastProvider.displayName = "ToastProvider";
var ToastViewport = React13.forwardRef(({ ...props }, ref) => /* @__PURE__ */ jsx13(
  ToastPrimitive.Viewport,
  {
    ref,
    className: viewportStyles2,
    ...props
  }
));
ToastViewport.displayName = "ToastViewport";
var ToastIcon = ({ variant }) => {
  const className = `size-5 shrink-0 ${iconStyles[variant || "default"]}`;
  switch (variant) {
    case "success":
      return /* @__PURE__ */ jsx13(k, { className, strokeWidth: 1 });
    case "warning":
      return /* @__PURE__ */ jsx13(D2, { className, strokeWidth: 1 });
    case "error":
      return /* @__PURE__ */ jsx13(R, { className, strokeWidth: 1 });
    default:
      return /* @__PURE__ */ jsx13(k, { className, strokeWidth: 1 });
  }
};
var Toast = React13.forwardRef(({ variant = "default", children, ...props }, ref) => /* @__PURE__ */ jsxs7(
  ToastPrimitive.Root,
  {
    ref,
    className: toastBaseStyles,
    ...props,
    children: [
      /* @__PURE__ */ jsx13(ToastIcon, { variant }),
      children
    ]
  }
));
Toast.displayName = "Toast";
var ToastTitle = React13.forwardRef(({ ...props }, ref) => /* @__PURE__ */ jsx13(
  ToastPrimitive.Title,
  {
    ref,
    className: titleStyles2,
    ...props
  }
));
ToastTitle.displayName = "ToastTitle";
var ToastDescription = React13.forwardRef(({ ...props }, ref) => /* @__PURE__ */ jsx13(
  ToastPrimitive.Description,
  {
    ref,
    className: descriptionStyles2,
    ...props
  }
));
ToastDescription.displayName = "ToastDescription";
var ToastAction = React13.forwardRef(({ children, ...props }, ref) => /* @__PURE__ */ jsx13(
  ToastPrimitive.Action,
  {
    ref,
    asChild: true,
    ...props,
    children: /* @__PURE__ */ jsx13(Button, { variant: "secondary", size: "sm", children })
  }
));
ToastAction.displayName = "ToastAction";
var ToastClose = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsxs7(
  ToastPrimitive.Close,
  {
    ref,
    className: className || "size-6 shrink-0 text-[#A9A9A9] hover:text-[#171717] dark:hover:text-white",
    "toast-close": "",
    ...props,
    children: [
      /* @__PURE__ */ jsx13("svg", { className: "size-4", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.5", children: /* @__PURE__ */ jsx13("path", { d: "M18 6L6 18M6 6l12 12" }) }),
      /* @__PURE__ */ jsx13("span", { className: "sr-only", children: "Close" })
    ]
  }
));
ToastClose.displayName = "ToastClose";

// src/toast/useToast.ts
import * as React14 from "react";
var TOAST_LIMIT = 5;
var TOAST_DURATION = 5e3;
var toasts = [];
var listeners = /* @__PURE__ */ new Set();
function notifyListeners() {
  const state = { toasts: [...toasts] };
  listeners.forEach((listener) => listener(state));
}
function generateId() {
  return Math.random().toString(36).substring(2, 9);
}
function addToast(options) {
  const id = generateId();
  const duration = options.duration ?? TOAST_DURATION;
  const newToast = {
    id,
    ...options,
    duration
  };
  toasts = [newToast, ...toasts].slice(0, TOAST_LIMIT);
  notifyListeners();
  if (duration > 0) {
    setTimeout(() => {
      dismissToast(id);
    }, duration);
  }
  return id;
}
function dismissToast(id) {
  toasts = toasts.filter((t8) => t8.id !== id);
  notifyListeners();
}
function dismissAll() {
  toasts = [];
  notifyListeners();
}
function toast(options) {
  if (typeof options === "string") {
    return addToast({ title: options });
  }
  return addToast(options);
}
toast.success = (title, options) => {
  return addToast({ ...options, title, variant: "success" });
};
toast.warning = (title, options) => {
  return addToast({ ...options, title, variant: "warning" });
};
toast.error = (title, options) => {
  return addToast({ ...options, title, variant: "error" });
};
toast.dismiss = dismissToast;
toast.dismissAll = dismissAll;
function useToast() {
  const [state, setState] = React14.useState({ toasts });
  React14.useEffect(() => {
    listeners.add(setState);
    return () => {
      listeners.delete(setState);
    };
  }, []);
  return {
    toasts: state.toasts,
    toast,
    dismiss: dismissToast,
    dismissAll
  };
}

// src/toast/Toaster.tsx
import { jsx as jsx14, jsxs as jsxs8 } from "react/jsx-runtime";
function Toaster({
  placement = "bottom-right"
}) {
  const { toasts: toasts2, dismiss } = useToast();
  const placementClass = viewportPlacementStyles[placement];
  return /* @__PURE__ */ jsxs8(ToastProvider, { swipeDirection: "right", children: [
    toasts2.map((t8) => /* @__PURE__ */ jsxs8(
      Toast,
      {
        variant: t8.variant,
        open: true,
        onOpenChange: (open) => {
          if (!open) dismiss(t8.id);
        },
        children: [
          /* @__PURE__ */ jsxs8("div", { className: "flex-1 min-w-0", children: [
            /* @__PURE__ */ jsx14(ToastTitle, { children: t8.title }),
            t8.description && /* @__PURE__ */ jsx14(ToastDescription, { children: t8.description })
          ] }),
          t8.action && /* @__PURE__ */ jsx14(
            ToastAction,
            {
              altText: t8.action.label,
              onClick: t8.action.onClick,
              children: t8.action.label
            }
          )
        ]
      },
      t8.id
    )),
    /* @__PURE__ */ jsx14(ToastViewport, { className: placementClass })
  ] });
}

// src/tooltip/Tooltip.tsx
import * as React15 from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

// src/tooltip/constants.ts
var contentStyles2 = [
  "z-50",
  "max-w-[280px]",
  // Theme-matched background (white on light, black on dark)
  "bg-white dark:bg-[#171717]",
  "text-[#171717] dark:text-white",
  // Subtle border
  "border border-[#E5E5E5] dark:border-[#333333]",
  // Spacing (8px grid)
  "px-3 py-2",
  // Typography - Caption scale
  "font-sans font-light",
  "text-[14px]",
  // Animation (100-200ms, ease-out per brand)
  "animate-in fade-in-0 zoom-in-95",
  "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
  "data-[side=bottom]:slide-in-from-top-2",
  "data-[side=left]:slide-in-from-right-2",
  "data-[side=right]:slide-in-from-left-2",
  "data-[side=top]:slide-in-from-bottom-2",
  "duration-150 ease-out"
].join(" ");
var SIDE_OFFSET_WITH_ARROW = 4;
var SIDE_OFFSET_WITHOUT_ARROW = 8;

// src/tooltip/Tooltip.tsx
import { jsx as jsx15, jsxs as jsxs9 } from "react/jsx-runtime";
var TooltipProvider = TooltipPrimitive.Provider;
TooltipProvider.displayName = "TooltipProvider";
var Tooltip = TooltipPrimitive.Root;
Tooltip.displayName = "Tooltip";
var TooltipTrigger = TooltipPrimitive.Trigger;
TooltipTrigger.displayName = "TooltipTrigger";
var TooltipContent = React15.forwardRef(({ sideOffset, showArrow = true, className, children, ...props }, ref) => {
  const defaultOffset = showArrow ? SIDE_OFFSET_WITH_ARROW : SIDE_OFFSET_WITHOUT_ARROW;
  return /* @__PURE__ */ jsx15(TooltipPrimitive.Portal, { children: /* @__PURE__ */ jsxs9(
    TooltipPrimitive.Content,
    {
      ref,
      sideOffset: sideOffset ?? defaultOffset,
      className: `${contentStyles2}${className ? ` ${className}` : ""}`,
      ...props,
      children: [
        children,
        showArrow && /* @__PURE__ */ jsx15(TooltipArrow, {})
      ]
    }
  ) });
});
TooltipContent.displayName = "TooltipContent";
var TooltipArrow = React15.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx15(TooltipPrimitive.Arrow, { asChild: true, ...props, children: /* @__PURE__ */ jsxs9(
  "svg",
  {
    ref,
    width: "12",
    height: "6",
    viewBox: "0 0 12 6",
    className: `-mt-px ${className || ""}`,
    children: [
      /* @__PURE__ */ jsx15(
        "path",
        {
          d: "M0 0L6 6L12 0",
          className: "fill-white dark:fill-[#171717]"
        }
      ),
      /* @__PURE__ */ jsx15(
        "path",
        {
          d: "M0 0L6 6L12 0",
          fill: "none",
          className: "stroke-[#E5E5E5] dark:stroke-[#333333]",
          strokeWidth: "1",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    ]
  }
) }));
TooltipArrow.displayName = "TooltipArrow";

// src/popover/Popover.tsx
import * as React16 from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";

// src/popover/constants.ts
var contentStyles3 = [
  "z-50",
  "w-72",
  "bg-white dark:bg-[#171717]",
  "text-[#171717] dark:text-white",
  "border border-[#E5E5E5] dark:border-[#333333]",
  "p-4",
  "outline-none",
  // Animation (100-200ms, ease-out)
  "data-[state=open]:animate-in data-[state=closed]:animate-out",
  "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
  "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
  "data-[side=bottom]:slide-in-from-top-2",
  "data-[side=left]:slide-in-from-right-2",
  "data-[side=right]:slide-in-from-left-2",
  "data-[side=top]:slide-in-from-bottom-2",
  "duration-200 ease-out"
].join(" ");
var closeButtonStyles2 = [
  "absolute right-3 top-3",
  "flex items-center justify-center",
  "text-[#A9A9A9]",
  "hover:text-[#171717] dark:hover:text-white",
  "transition-colors duration-200",
  "focus:outline-none"
].join(" ");

// src/popover/Popover.tsx
import { Fragment, jsx as jsx16, jsxs as jsxs10 } from "react/jsx-runtime";
var Popover = PopoverPrimitive.Root;
Popover.displayName = "Popover";
var PopoverTrigger = PopoverPrimitive.Trigger;
PopoverTrigger.displayName = "PopoverTrigger";
var PopoverAnchor = PopoverPrimitive.Anchor;
PopoverAnchor.displayName = "PopoverAnchor";
var PopoverContent = React16.forwardRef(({ align = "center", sideOffset = 8, showArrow = false, className, children, ...props }, ref) => /* @__PURE__ */ jsx16(PopoverPrimitive.Portal, { children: /* @__PURE__ */ jsxs10(
  PopoverPrimitive.Content,
  {
    ref,
    align,
    sideOffset,
    className: `${contentStyles3}${className ? ` ${className}` : ""}`,
    ...props,
    children: [
      children,
      showArrow && /* @__PURE__ */ jsx16(PopoverArrow, {})
    ]
  }
) }));
PopoverContent.displayName = "PopoverContent";
var PopoverArrow = React16.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx16(PopoverPrimitive.Arrow, { asChild: true, ...props, children: /* @__PURE__ */ jsxs10(
  "svg",
  {
    ref,
    width: "12",
    height: "6",
    viewBox: "0 0 12 6",
    className: `-mt-px ${className || ""}`,
    children: [
      /* @__PURE__ */ jsx16(
        "path",
        {
          d: "M0 0L6 6L12 0",
          className: "fill-white dark:fill-[#171717]"
        }
      ),
      /* @__PURE__ */ jsx16(
        "path",
        {
          d: "M0 0L6 6L12 0",
          fill: "none",
          className: "stroke-[#E5E5E5] dark:stroke-[#333333]",
          strokeWidth: "1",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    ]
  }
) }));
PopoverArrow.displayName = "PopoverArrow";
var PopoverClose = React16.forwardRef(({ children, ...props }, ref) => /* @__PURE__ */ jsx16(
  PopoverPrimitive.Close,
  {
    ref,
    className: closeButtonStyles2,
    ...props,
    children: children || /* @__PURE__ */ jsxs10(Fragment, { children: [
      /* @__PURE__ */ jsx16(R, { className: "size-5", strokeWidth: 1 }),
      /* @__PURE__ */ jsx16("span", { className: "sr-only", children: "Close" })
    ] })
  }
));
PopoverClose.displayName = "PopoverClose";

// src/dropdown-menu/DropdownMenu.tsx
import * as React17 from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";

// src/dropdown-menu/constants.ts
var contentStyles4 = [
  "z-50 min-w-[180px]",
  "overflow-hidden",
  "bg-white dark:bg-[#171717]",
  "border border-[#E5E5E5] dark:border-[#333333]",
  "p-1",
  // Animation
  "data-[state=open]:animate-in data-[state=closed]:animate-out",
  "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
  "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
  "data-[side=bottom]:slide-in-from-top-2",
  "data-[side=left]:slide-in-from-right-2",
  "data-[side=right]:slide-in-from-left-2",
  "data-[side=top]:slide-in-from-bottom-2",
  "duration-200 ease-out"
].join(" ");
var subContentStyles = [
  "z-50 min-w-[180px]",
  "overflow-hidden",
  "bg-white dark:bg-[#171717]",
  "border border-[#E5E5E5] dark:border-[#333333]",
  "p-1",
  // Animation
  "data-[state=open]:animate-in data-[state=closed]:animate-out",
  "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
  "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
  "data-[side=bottom]:slide-in-from-top-2",
  "data-[side=left]:slide-in-from-right-2",
  "data-[side=right]:slide-in-from-left-2",
  "data-[side=top]:slide-in-from-bottom-2",
  "duration-200 ease-out"
].join(" ");
var itemBaseStyles2 = [
  "relative flex items-center gap-2",
  "cursor-pointer select-none",
  "px-3 py-2",
  "font-sans font-light text-[14px]",
  "text-[#171717] dark:text-white",
  "outline-none",
  "transition-colors duration-100",
  "focus:bg-[#FAFAFA] dark:focus:bg-[#222222]",
  "data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
].join(" ");
var itemDangerStyles = [
  "text-[#FF4C00]",
  "focus:text-[#FF4C00]",
  "focus:bg-[#FF4C00]/10"
].join(" ");
var subTriggerStyles = [
  "relative flex items-center gap-2",
  "cursor-pointer select-none",
  "px-3 py-2",
  "font-sans font-light text-[14px]",
  "text-[#171717] dark:text-white",
  "outline-none",
  "transition-colors duration-100",
  "focus:bg-[#FAFAFA] dark:focus:bg-[#222222]",
  "data-[state=open]:bg-[#FAFAFA] dark:data-[state=open]:bg-[#222222]"
].join(" ");
var checkboxItemStyles = [
  "relative flex items-center gap-2",
  "cursor-pointer select-none",
  "pl-8 pr-3 py-2",
  "font-sans font-light text-[14px]",
  "text-[#171717] dark:text-white",
  "outline-none",
  "transition-colors duration-100",
  "focus:bg-[#FAFAFA] dark:focus:bg-[#222222]",
  "data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
].join(" ");
var labelStyles2 = [
  "px-3 py-2",
  "font-mono font-extralight uppercase tracking-wide",
  "text-[14px] lg:text-[18px]",
  "text-[#A9A9A9]"
].join(" ");
var separatorStyles2 = [
  "-mx-1 my-1",
  "h-px",
  "bg-[#E5E5E5] dark:bg-[#333333]"
].join(" ");
var shortcutStyles = [
  "ml-auto",
  "font-mono font-extralight text-[14px]",
  "text-[#A9A9A9]"
].join(" ");
var indicatorStyles3 = [
  "absolute left-3",
  "flex items-center justify-center",
  "text-[#3941EB] dark:text-[#00FBA9]"
].join(" ");

// src/dropdown-menu/DropdownMenu.tsx
import { jsx as jsx17, jsxs as jsxs11 } from "react/jsx-runtime";
var DropdownMenu = DropdownMenuPrimitive.Root;
DropdownMenu.displayName = "DropdownMenu";
var DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
DropdownMenuTrigger.displayName = "DropdownMenuTrigger";
var DropdownMenuGroup = DropdownMenuPrimitive.Group;
DropdownMenuGroup.displayName = "DropdownMenuGroup";
var DropdownMenuSub = DropdownMenuPrimitive.Sub;
DropdownMenuSub.displayName = "DropdownMenuSub";
var DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;
DropdownMenuRadioGroup.displayName = "DropdownMenuRadioGroup";
var DropdownMenuContent = React17.forwardRef(({ sideOffset = 4, className, ...props }, ref) => /* @__PURE__ */ jsx17(DropdownMenuPrimitive.Portal, { children: /* @__PURE__ */ jsx17(
  DropdownMenuPrimitive.Content,
  {
    ref,
    sideOffset,
    className: `${contentStyles4}${className ? ` ${className}` : ""}`,
    ...props
  }
) }));
DropdownMenuContent.displayName = "DropdownMenuContent";
var DropdownMenuSubContent = React17.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx17(DropdownMenuPrimitive.Portal, { children: /* @__PURE__ */ jsx17(
  DropdownMenuPrimitive.SubContent,
  {
    ref,
    className: `${subContentStyles}${className ? ` ${className}` : ""}`,
    ...props
  }
) }));
DropdownMenuSubContent.displayName = "DropdownMenuSubContent";
var DropdownMenuItem = React17.forwardRef(({ variant = "default", icon, children, ...props }, ref) => /* @__PURE__ */ jsxs11(
  DropdownMenuPrimitive.Item,
  {
    ref,
    className: `${itemBaseStyles2} ${variant === "danger" ? itemDangerStyles : ""}`,
    ...props,
    children: [
      icon && /* @__PURE__ */ jsx17("span", { className: "size-4 shrink-0", children: icon }),
      children
    ]
  }
));
DropdownMenuItem.displayName = "DropdownMenuItem";
var DropdownMenuSubTrigger = React17.forwardRef(({ children, ...props }, ref) => /* @__PURE__ */ jsxs11(
  DropdownMenuPrimitive.SubTrigger,
  {
    ref,
    className: subTriggerStyles,
    ...props,
    children: [
      children,
      /* @__PURE__ */ jsx17(g2, { className: "ml-auto size-4", strokeWidth: 1 })
    ]
  }
));
DropdownMenuSubTrigger.displayName = "DropdownMenuSubTrigger";
var DropdownMenuCheckboxItem = React17.forwardRef(({ children, checked, ...props }, ref) => /* @__PURE__ */ jsxs11(
  DropdownMenuPrimitive.CheckboxItem,
  {
    ref,
    className: checkboxItemStyles,
    checked,
    ...props,
    children: [
      /* @__PURE__ */ jsx17("span", { className: indicatorStyles3, children: /* @__PURE__ */ jsx17(DropdownMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx17(k, { className: "size-4", strokeWidth: 1 }) }) }),
      children
    ]
  }
));
DropdownMenuCheckboxItem.displayName = "DropdownMenuCheckboxItem";
var DropdownMenuRadioItem = React17.forwardRef(({ children, ...props }, ref) => /* @__PURE__ */ jsxs11(
  DropdownMenuPrimitive.RadioItem,
  {
    ref,
    className: checkboxItemStyles,
    ...props,
    children: [
      /* @__PURE__ */ jsx17("span", { className: indicatorStyles3, children: /* @__PURE__ */ jsx17(DropdownMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx17("svg", { className: "size-2 fill-current", viewBox: "0 0 8 8", children: /* @__PURE__ */ jsx17("circle", { cx: "4", cy: "4", r: "4" }) }) }) }),
      children
    ]
  }
));
DropdownMenuRadioItem.displayName = "DropdownMenuRadioItem";
var DropdownMenuLabel = React17.forwardRef(({ ...props }, ref) => /* @__PURE__ */ jsx17(
  DropdownMenuPrimitive.Label,
  {
    ref,
    className: labelStyles2,
    ...props
  }
));
DropdownMenuLabel.displayName = "DropdownMenuLabel";
var DropdownMenuSeparator = React17.forwardRef(({ ...props }, ref) => /* @__PURE__ */ jsx17(
  DropdownMenuPrimitive.Separator,
  {
    ref,
    className: separatorStyles2,
    ...props
  }
));
DropdownMenuSeparator.displayName = "DropdownMenuSeparator";
var DropdownMenuShortcut = ({ ...props }) => /* @__PURE__ */ jsx17("span", { className: shortcutStyles, ...props });
DropdownMenuShortcut.displayName = "DropdownMenuShortcut";

// src/tabs/Tabs.tsx
import * as React18 from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";

// src/tabs/constants.ts
var listStyles = [
  "relative",
  "inline-flex items-center",
  "h-12",
  "bg-[#FAFAFA] dark:bg-white/5",
  "p-1",
  "gap-1"
].join(" ");
var indicatorStyles4 = [
  "absolute",
  "h-10",
  "bg-white dark:bg-[#171717]",
  "dark:border dark:border-[#333333]",
  "transition-all duration-200 ease-out",
  "pointer-events-none"
].join(" ");
var triggerStyles = [
  "relative z-10",
  "inline-flex items-center justify-center gap-2",
  "whitespace-nowrap",
  "px-4 h-10",
  // Typography - mono for labels/CTAs (Caption scale, Extra Light 200)
  "font-mono font-extralight uppercase tracking-wide",
  "text-[14px] lg:text-[18px]",
  "text-[#A9A9A9]",
  // States
  "transition-colors duration-200",
  "hover:text-[#171717] dark:hover:text-white",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3941EB] dark:focus-visible:ring-[#00FBA9]",
  "disabled:pointer-events-none disabled:opacity-50",
  // Active state - text only (background handled by indicator)
  "data-[state=active]:text-[#171717] dark:data-[state=active]:text-white"
].join(" ");
var contentStyles5 = [
  "mt-4",
  "focus-visible:outline-none"
].join(" ");

// src/tabs/Tabs.tsx
import { jsx as jsx18, jsxs as jsxs12 } from "react/jsx-runtime";
var Tabs = TabsPrimitive.Root;
Tabs.displayName = "Tabs";
var TabsList = React18.forwardRef(({ children, ...props }, ref) => {
  const listRef = React18.useRef(null);
  const [indicatorStyle, setIndicatorStyle] = React18.useState({ left: 0, width: 0 });
  React18.useEffect(() => {
    const updateIndicator = () => {
      const list = listRef.current;
      if (!list) return;
      const activeTab = list.querySelector('[data-state="active"]');
      if (activeTab) {
        setIndicatorStyle({
          left: activeTab.offsetLeft,
          width: activeTab.offsetWidth
        });
      }
    };
    updateIndicator();
    const observer = new MutationObserver(updateIndicator);
    if (listRef.current) {
      observer.observe(listRef.current, {
        attributes: true,
        subtree: true,
        attributeFilter: ["data-state"]
      });
    }
    window.addEventListener("resize", updateIndicator);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateIndicator);
    };
  }, []);
  return /* @__PURE__ */ jsxs12(
    TabsPrimitive.List,
    {
      ref: (node) => {
        listRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      },
      className: listStyles,
      ...props,
      children: [
        /* @__PURE__ */ jsx18(
          "div",
          {
            className: indicatorStyles4,
            style: {
              left: indicatorStyle.left,
              width: indicatorStyle.width
            }
          }
        ),
        children
      ]
    }
  );
});
TabsList.displayName = "TabsList";
var TabsTrigger = React18.forwardRef(({ icon, children, ...props }, ref) => /* @__PURE__ */ jsxs12(
  TabsPrimitive.Trigger,
  {
    ref,
    className: triggerStyles,
    ...props,
    children: [
      icon && /* @__PURE__ */ jsx18("span", { className: "size-4 shrink-0", children: icon }),
      children
    ]
  }
));
TabsTrigger.displayName = "TabsTrigger";
var TabsContent = React18.forwardRef(({ ...props }, ref) => /* @__PURE__ */ jsx18(
  TabsPrimitive.Content,
  {
    ref,
    className: contentStyles5,
    ...props
  }
));
TabsContent.displayName = "TabsContent";

// src/card/Card.tsx
import * as React19 from "react";
import { Slot as Slot2 } from "@radix-ui/react-slot";

// src/card/constants.ts
var cardBaseStyles = [
  "flex flex-col",
  "text-[#171717] dark:text-white"
].join(" ");
var cardVariantStyles = {
  default: "bg-[#FAFAFA] dark:bg-[#171717] dark:border dark:border-[#333333]",
  outlined: "bg-transparent border border-[#E5E5E5] dark:border-[#333333]"
};
var cardPaddingStyles = {
  none: "",
  sm: "p-4",
  md: "p-6",
  lg: "p-8"
};
var headerStyles2 = [
  "flex flex-col gap-1"
].join(" ");
var titleStyles3 = [
  "font-sans font-light",
  "text-[23px] lg:text-[29px]",
  "leading-[1.1]",
  "text-[#171717] dark:text-white"
].join(" ");
var descriptionStyles3 = [
  "font-sans font-light",
  "text-[14px] lg:text-[18px]",
  "text-[#A9A9A9]"
].join(" ");
var contentStyles6 = "";
var footerStyles2 = [
  "flex items-center gap-4"
].join(" ");

// src/card/Card.tsx
import { jsx as jsx19 } from "react/jsx-runtime";
var Card = React19.forwardRef(
  ({ variant = "default", padding = "md", asChild = false, className, ...props }, ref) => {
    const Comp = asChild ? Slot2 : "div";
    return /* @__PURE__ */ jsx19(
      Comp,
      {
        ref,
        className: `${cardBaseStyles} ${cardVariantStyles[variant]} ${cardPaddingStyles[padding]}${className ? ` ${className}` : ""}`,
        ...props
      }
    );
  }
);
Card.displayName = "Card";
var CardHeader = React19.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx19(
    "div",
    {
      ref,
      className: `${headerStyles2}${className ? ` ${className}` : ""}`,
      ...props
    }
  )
);
CardHeader.displayName = "CardHeader";
var CardTitle = React19.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx19(
    "h3",
    {
      ref,
      className: `${titleStyles3}${className ? ` ${className}` : ""}`,
      ...props
    }
  )
);
CardTitle.displayName = "CardTitle";
var CardDescription = React19.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx19(
    "p",
    {
      ref,
      className: `${descriptionStyles3}${className ? ` ${className}` : ""}`,
      ...props
    }
  )
);
CardDescription.displayName = "CardDescription";
var CardContent = React19.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx19(
    "div",
    {
      ref,
      className: `${contentStyles6}${className ? ` ${className}` : ""}`,
      ...props
    }
  )
);
CardContent.displayName = "CardContent";
var CardFooter = React19.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx19(
    "div",
    {
      ref,
      className: `${footerStyles2}${className ? ` ${className}` : ""}`,
      ...props
    }
  )
);
CardFooter.displayName = "CardFooter";

// src/breadcrumb/Breadcrumb.tsx
import * as React20 from "react";
import { Slot as Slot3 } from "@radix-ui/react-slot";

// src/breadcrumb/constants.ts
var navStyles = "";
var listStyles2 = [
  "flex flex-wrap items-center gap-2"
].join(" ");
var itemStyles2 = [
  "inline-flex items-center gap-2"
].join(" ");
var linkStyles = [
  "font-sans font-light",
  "text-[14px] lg:text-[18px]",
  "text-[#A9A9A9]",
  "transition-colors duration-200",
  "hover:text-[#171717] dark:hover:text-white"
].join(" ");
var pageStyles = [
  "font-sans font-light",
  "text-[14px] lg:text-[18px]",
  "text-[#171717] dark:text-white"
].join(" ");
var separatorStyles3 = [
  "text-[#A9A9A9]",
  "[&>svg]:size-4"
].join(" ");
var ellipsisStyles = [
  "flex items-center justify-center",
  "size-4",
  "text-[#A9A9A9]"
].join(" ");

// src/breadcrumb/Breadcrumb.tsx
import { jsx as jsx20, jsxs as jsxs13 } from "react/jsx-runtime";
var Breadcrumb = React20.forwardRef(
  ({ "aria-label": ariaLabel = "Breadcrumb", className, ...props }, ref) => /* @__PURE__ */ jsx20(
    "nav",
    {
      ref,
      "aria-label": ariaLabel,
      className: `${navStyles}${className ? ` ${className}` : ""}`,
      ...props
    }
  )
);
Breadcrumb.displayName = "Breadcrumb";
var BreadcrumbList = React20.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx20(
    "ol",
    {
      ref,
      className: `${listStyles2}${className ? ` ${className}` : ""}`,
      ...props
    }
  )
);
BreadcrumbList.displayName = "BreadcrumbList";
var BreadcrumbItem = React20.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx20(
    "li",
    {
      ref,
      className: `${itemStyles2}${className ? ` ${className}` : ""}`,
      ...props
    }
  )
);
BreadcrumbItem.displayName = "BreadcrumbItem";
var BreadcrumbLink = React20.forwardRef(
  ({ asChild = false, className, ...props }, ref) => {
    const Comp = asChild ? Slot3 : "a";
    return /* @__PURE__ */ jsx20(
      Comp,
      {
        ref,
        className: `${linkStyles}${className ? ` ${className}` : ""}`,
        ...props
      }
    );
  }
);
BreadcrumbLink.displayName = "BreadcrumbLink";
var BreadcrumbPage = React20.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx20(
    "span",
    {
      ref,
      role: "link",
      "aria-disabled": "true",
      "aria-current": "page",
      className: `${pageStyles}${className ? ` ${className}` : ""}`,
      ...props
    }
  )
);
BreadcrumbPage.displayName = "BreadcrumbPage";
var BreadcrumbSeparator = React20.forwardRef(
  ({ children, className, ...props }, ref) => /* @__PURE__ */ jsx20(
    "li",
    {
      ref,
      role: "presentation",
      "aria-hidden": "true",
      className: `${separatorStyles3}${className ? ` ${className}` : ""}`,
      ...props,
      children: children ?? /* @__PURE__ */ jsx20(g2, { strokeWidth: 1 })
    }
  )
);
BreadcrumbSeparator.displayName = "BreadcrumbSeparator";
var BreadcrumbEllipsis = React20.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsxs13(
    "span",
    {
      ref,
      role: "presentation",
      "aria-hidden": "true",
      className: `${ellipsisStyles}${className ? ` ${className}` : ""}`,
      ...props,
      children: [
        /* @__PURE__ */ jsx20(D3, { className: "size-4", strokeWidth: 1 }),
        /* @__PURE__ */ jsx20("span", { className: "sr-only", children: "More" })
      ]
    }
  )
);
BreadcrumbEllipsis.displayName = "BreadcrumbEllipsis";

// src/pagination/Pagination.tsx
import * as React21 from "react";
import { Slot as Slot4 } from "@radix-ui/react-slot";

// src/pagination/constants.ts
var navStyles2 = [
  "flex justify-center"
].join(" ");
var contentStyles7 = [
  "flex items-center gap-1"
].join(" ");
var itemStyles3 = "";
var linkBaseStyles = [
  "inline-flex items-center justify-center",
  "size-10",
  "font-sans font-light text-[14px] lg:text-[18px]",
  "text-[#A9A9A9]",
  "transition-colors duration-200",
  "hover:text-[#171717] dark:hover:text-white",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3941EB] dark:focus-visible:ring-[#00FBA9]"
].join(" ");
var linkActiveStyles = [
  "bg-[#171717] dark:bg-white",
  "text-white dark:text-[#171717]",
  "hover:text-white dark:hover:text-[#171717]"
].join(" ");
var navButtonStyles = [
  "inline-flex items-center gap-2",
  "px-4 h-10",
  "font-mono font-extralight text-[14px] lg:text-[18px] uppercase tracking-wide",
  "text-[#A9A9A9]",
  "transition-colors duration-200",
  "hover:text-[#171717] dark:hover:text-white",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3941EB] dark:focus-visible:ring-[#00FBA9]",
  "aria-disabled:pointer-events-none aria-disabled:opacity-50"
].join(" ");
var ellipsisStyles2 = [
  "flex items-center justify-center",
  "size-10",
  "text-[#A9A9A9]"
].join(" ");

// src/pagination/Pagination.tsx
import { jsx as jsx21, jsxs as jsxs14 } from "react/jsx-runtime";
var Pagination = React21.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx21(
    "nav",
    {
      ref,
      role: "navigation",
      "aria-label": "Pagination",
      className: `${navStyles2}${className ? ` ${className}` : ""}`,
      ...props
    }
  )
);
Pagination.displayName = "Pagination";
var PaginationContent = React21.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx21(
    "ul",
    {
      ref,
      className: `${contentStyles7}${className ? ` ${className}` : ""}`,
      ...props
    }
  )
);
PaginationContent.displayName = "PaginationContent";
var PaginationItem = React21.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx21(
    "li",
    {
      ref,
      className: `${itemStyles3}${className ? ` ${className}` : ""}`,
      ...props
    }
  )
);
PaginationItem.displayName = "PaginationItem";
var PaginationLink = React21.forwardRef(
  ({ isActive = false, asChild = false, className, ...props }, ref) => {
    const Comp = asChild ? Slot4 : "a";
    return /* @__PURE__ */ jsx21(
      Comp,
      {
        ref,
        "aria-current": isActive ? "page" : void 0,
        className: `${linkBaseStyles} ${isActive ? linkActiveStyles : ""}${className ? ` ${className}` : ""}`,
        ...props
      }
    );
  }
);
PaginationLink.displayName = "PaginationLink";
var PaginationPrevious = React21.forwardRef(
  ({ asChild = false, className, children, ...props }, ref) => {
    const Comp = asChild ? Slot4 : "a";
    return /* @__PURE__ */ jsxs14(
      Comp,
      {
        ref,
        "aria-label": "Go to previous page",
        className: `${navButtonStyles}${className ? ` ${className}` : ""}`,
        ...props,
        children: [
          /* @__PURE__ */ jsx21(L2, { className: "size-4", strokeWidth: 1 }),
          children ?? /* @__PURE__ */ jsx21("span", { children: "Previous" })
        ]
      }
    );
  }
);
PaginationPrevious.displayName = "PaginationPrevious";
var PaginationNext = React21.forwardRef(
  ({ asChild = false, className, children, ...props }, ref) => {
    const Comp = asChild ? Slot4 : "a";
    return /* @__PURE__ */ jsxs14(
      Comp,
      {
        ref,
        "aria-label": "Go to next page",
        className: `${navButtonStyles}${className ? ` ${className}` : ""}`,
        ...props,
        children: [
          children ?? /* @__PURE__ */ jsx21("span", { children: "Next" }),
          /* @__PURE__ */ jsx21(g2, { className: "size-4", strokeWidth: 1 })
        ]
      }
    );
  }
);
PaginationNext.displayName = "PaginationNext";
var PaginationEllipsis = React21.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsxs14(
    "span",
    {
      ref,
      "aria-hidden": "true",
      className: `${ellipsisStyles2}${className ? ` ${className}` : ""}`,
      ...props,
      children: [
        /* @__PURE__ */ jsx21(D3, { className: "size-4", strokeWidth: 1 }),
        /* @__PURE__ */ jsx21("span", { className: "sr-only", children: "More pages" })
      ]
    }
  )
);
PaginationEllipsis.displayName = "PaginationEllipsis";

// src/alert/Alert.tsx
import * as React22 from "react";

// src/alert/constants.ts
var alertBaseStyles = [
  "relative flex gap-3",
  "w-full p-4",
  "font-sans",
  "[&>svg]:size-5 [&>svg]:shrink-0 [&>svg]:mt-0.5"
].join(" ");
var alertVariantStyles = {
  default: [
    "bg-[#FAFAFA] dark:bg-[#171717]",
    "border border-[#E5E5E5] dark:border-[#333333]",
    "text-[#171717] dark:text-white",
    "[&>svg]:text-[#171717] dark:[&>svg]:text-white"
  ].join(" "),
  success: [
    "bg-[#3941EB]/10 dark:bg-[#00FBA9]/10",
    "border border-[#3941EB]/20 dark:border-[#00FBA9]/20",
    "text-[#171717] dark:text-white",
    "[&>svg]:text-[#3941EB] dark:[&>svg]:text-[#00FBA9]"
  ].join(" "),
  warning: [
    "bg-[#FF4C00]/10",
    "border border-[#FF4C00]/20",
    "text-[#171717] dark:text-white",
    "[&>svg]:text-[#FF4C00]"
  ].join(" "),
  error: [
    "bg-[#FF4C00]/10",
    "border border-[#FF4C00]/20",
    "text-[#171717] dark:text-white",
    "[&>svg]:text-[#FF4C00]"
  ].join(" ")
};
var titleStyles4 = [
  "font-sans font-light",
  "text-[14px] lg:text-[18px]",
  "leading-[1.4]",
  "text-[#171717] dark:text-white"
].join(" ");
var descriptionStyles4 = [
  "font-sans font-light",
  "text-[14px] lg:text-[18px]",
  "text-[#A9A9A9]"
].join(" ");

// src/alert/Alert.tsx
import { jsx as jsx22, jsxs as jsxs15 } from "react/jsx-runtime";
var variantIcons = {
  default: /* @__PURE__ */ jsx22(C4, { strokeWidth: 1 }),
  success: /* @__PURE__ */ jsx22(k, { strokeWidth: 1 }),
  warning: /* @__PURE__ */ jsx22(D2, { strokeWidth: 1 }),
  error: /* @__PURE__ */ jsx22(R, { strokeWidth: 1 })
};
var Alert = React22.forwardRef(
  ({ variant = "default", children, ...props }, ref) => /* @__PURE__ */ jsxs15(
    "div",
    {
      ref,
      role: "alert",
      className: `${alertBaseStyles} ${alertVariantStyles[variant]}`,
      ...props,
      children: [
        variantIcons[variant],
        /* @__PURE__ */ jsx22("div", { className: "flex-1", children })
      ]
    }
  )
);
Alert.displayName = "Alert";
var AlertTitle = React22.forwardRef(
  (props, ref) => /* @__PURE__ */ jsx22(
    "h5",
    {
      ref,
      className: titleStyles4,
      ...props
    }
  )
);
AlertTitle.displayName = "AlertTitle";
var AlertDescription = React22.forwardRef(
  (props, ref) => /* @__PURE__ */ jsx22(
    "p",
    {
      ref,
      className: descriptionStyles4,
      ...props
    }
  )
);
AlertDescription.displayName = "AlertDescription";

// src/progress/Progress.tsx
import * as React23 from "react";

// src/progress/constants.ts
var containerStyles = [
  "relative w-full h-2",
  "bg-[#E5E5E5] dark:bg-[#333333]",
  "overflow-hidden"
].join(" ");
var indicatorVariantStyles = {
  default: "bg-[#3941EB] dark:bg-[#00FBA9]",
  success: "bg-[#3941EB] dark:bg-[#00FBA9]",
  warning: "bg-[#FF4C00]",
  error: "bg-[#FF4C00]"
};
var indicatorBaseStyles = [
  "h-full",
  "transition-[width] duration-300 ease-out"
].join(" ");
var labelStyles3 = [
  "font-mono font-extralight uppercase",
  "text-[14px] lg:text-[18px]",
  "text-[#A9A9A9]",
  "mt-2"
].join(" ");

// src/progress/Progress.tsx
import { jsx as jsx23, jsxs as jsxs16 } from "react/jsx-runtime";
var Progress = React23.forwardRef(
  ({ value = 0, variant = "default", showValue = false, ...props }, ref) => {
    const clampedValue = Math.min(100, Math.max(0, value));
    return /* @__PURE__ */ jsxs16("div", { children: [
      /* @__PURE__ */ jsx23(
        "div",
        {
          ref,
          role: "progressbar",
          "aria-valuenow": clampedValue,
          "aria-valuemin": 0,
          "aria-valuemax": 100,
          className: containerStyles,
          ...props,
          children: /* @__PURE__ */ jsx23(
            "div",
            {
              className: `${indicatorBaseStyles} ${indicatorVariantStyles[variant]}`,
              style: { width: `${clampedValue}%` }
            }
          )
        }
      ),
      showValue && /* @__PURE__ */ jsxs16("div", { className: labelStyles3, children: [
        clampedValue,
        "%"
      ] })
    ] });
  }
);
Progress.displayName = "Progress";

// src/separator/Separator.tsx
import * as React24 from "react";

// src/separator/constants.ts
var baseStyles7 = "shrink-0 bg-[#E5E5E5] dark:bg-[#333333]";
var orientationStyles = {
  horizontal: "h-px w-full",
  vertical: "h-full w-px"
};

// src/separator/Separator.tsx
import { jsx as jsx24 } from "react/jsx-runtime";
var Separator3 = React24.forwardRef(
  ({ orientation = "horizontal", decorative = true, ...props }, ref) => /* @__PURE__ */ jsx24(
    "div",
    {
      ref,
      role: decorative ? "none" : "separator",
      "aria-orientation": decorative ? void 0 : orientation,
      className: `${baseStyles7} ${orientationStyles[orientation]}`,
      ...props
    }
  )
);
Separator3.displayName = "Separator";

// src/skeleton/Skeleton.tsx
import * as React25 from "react";

// src/skeleton/constants.ts
var skeletonStyles = [
  "animate-pulse",
  "bg-[#E5E5E5] dark:bg-[#333333]"
].join(" ");

// src/skeleton/Skeleton.tsx
import { jsx as jsx25 } from "react/jsx-runtime";
var Skeleton = React25.forwardRef(
  ({ width, height, circle = false, style, ...props }, ref) => /* @__PURE__ */ jsx25(
    "div",
    {
      ref,
      className: skeletonStyles,
      style: {
        width,
        height,
        borderRadius: circle ? "9999px" : void 0,
        ...style
      },
      ...props
    }
  )
);
Skeleton.displayName = "Skeleton";

// src/componentMeta.ts
var componentMeta = {
  // Accordion
  Accordion: {
    description: "Collapsible content sections for organizing information.",
    props: {},
    subComponents: ["AccordionItem", "AccordionTrigger", "AccordionContent"]
  },
  // AccordionContent
  AccordionContent: {
    description: "Content that is revealed when accordion item is open.",
    props: {}
  },
  // AccordionItem
  AccordionItem: {
    description: "Individual item in an accordion.",
    props: {}
  },
  // AccordionTrigger
  AccordionTrigger: {
    description: "Clickable trigger that toggles an accordion item.",
    props: {}
  },
  // Alert
  Alert: {
    description: "Alert component for displaying important messages.",
    props: {
      variant: {
        type: "enum",
        values: ["default", "success", "warning", "error"],
        description: "Visual style variant",
        optional: true
      }
    },
    subComponents: ["AlertTitle", "AlertDescription"]
  },
  // AlertDescription
  AlertDescription: {
    description: "Description text for an alert.",
    props: {}
  },
  // AlertTitle
  AlertTitle: {
    description: "Title of an alert.",
    props: {}
  },
  // Avatar
  Avatar: {
    description: "User avatar with image and fallback support.",
    props: {
      size: {
        type: "enum",
        values: ["sm", "md", "lg", "xl"],
        description: "Size of the avatar",
        optional: true
      }
    },
    subComponents: ["AvatarImage", "AvatarFallback"]
  },
  // AvatarFallback
  AvatarFallback: {
    description: "Fallback content shown when Avatar image fails to load.",
    props: {}
  },
  // AvatarImage
  AvatarImage: {
    description: "Image component for Avatar.",
    props: {}
  },
  // Badge
  Badge: {
    description: "Status badge for displaying labels, tags, or status indicators.",
    props: {
      variant: {
        type: "enum",
        values: ["default", "success", "warning", "error"],
        description: "Visual variant of the badge",
        optional: true
      },
      size: {
        type: "enum",
        values: ["sm", "md"],
        description: "Size of the badge",
        optional: true
      },
      icon: {
        type: "ReactNode",
        description: "Icon to display before the text (12x12px)",
        optional: true
      },
      children: {
        type: "ReactNode",
        description: "Badge content"
      }
    }
  },
  // Breadcrumb
  Breadcrumb: {
    description: "Breadcrumb navigation component for showing page hierarchy.",
    props: {
      "aria-label": {
        type: "string",
        description: "Accessible label",
        optional: true
      }
    },
    subComponents: ["BreadcrumbList", "BreadcrumbItem", "BreadcrumbLink", "BreadcrumbPage", "BreadcrumbSeparator", "BreadcrumbEllipsis"]
  },
  // BreadcrumbEllipsis
  BreadcrumbEllipsis: {
    description: "Ellipsis indicator for collapsed breadcrumb items.",
    props: {}
  },
  // BreadcrumbItem
  BreadcrumbItem: {
    description: "Individual item in the breadcrumb trail.",
    props: {}
  },
  // BreadcrumbLink
  BreadcrumbLink: {
    description: "Link element for navigable breadcrumb items.",
    props: {
      asChild: {
        type: "boolean",
        description: "Render as child element (for router links)",
        optional: true
      }
    }
  },
  // BreadcrumbList
  BreadcrumbList: {
    description: "Ordered list container for breadcrumb items.",
    props: {}
  },
  // BreadcrumbPage
  BreadcrumbPage: {
    description: "Current page indicator (not a link).",
    props: {}
  },
  // BreadcrumbSeparator
  BreadcrumbSeparator: {
    description: "Separator between breadcrumb items.",
    props: {
      children: {
        type: "ReactNode",
        description: "Custom separator content",
        optional: true
      }
    }
  },
  // Button
  Button: {
    description: "Button component with variants for primary actions, secondary actions, and destructive actions.",
    props: {
      variant: {
        type: "enum",
        values: ["primary", "secondary", "danger", "ghost"],
        description: "Visual variant of the button",
        optional: true
      },
      size: {
        type: "enum",
        values: ["sm", "md", "lg"],
        description: "Size of the button",
        optional: true
      },
      asChild: {
        type: "boolean",
        description: "Use Radix Slot for composition (render as child element)",
        optional: true
      },
      children: {
        type: "ReactNode",
        description: "Button content (optional if using icon-only variant)",
        optional: true
      },
      iconLeft: {
        type: "ReactNode",
        description: "Icon to display on the left side of the button text",
        optional: true
      },
      iconRight: {
        type: "ReactNode",
        description: "Icon to display on the right side of the button text",
        optional: true
      },
      loading: {
        type: "boolean",
        description: "Shows a spinner and disables the button",
        optional: true
      }
    }
  },
  // Card
  Card: {
    description: "Container component for grouping related content with optional header, content, and footer sections.",
    props: {
      variant: {
        type: "enum",
        values: ["default", "outlined"],
        description: "Visual style variant",
        optional: true
      },
      padding: {
        type: "enum",
        values: ["none", "sm", "md", "lg"],
        description: "Internal padding",
        optional: true
      },
      asChild: {
        type: "boolean",
        description: "Render as child element",
        optional: true
      }
    },
    subComponents: ["CardHeader", "CardTitle", "CardDescription", "CardContent", "CardFooter"]
  },
  // CardContent
  CardContent: {
    description: "Main content area of a Card.",
    props: {}
  },
  // CardDescription
  CardDescription: {
    description: "Description text for Card header.",
    props: {}
  },
  // CardFooter
  CardFooter: {
    description: "Footer section of a Card, typically for actions.",
    props: {}
  },
  // CardHeader
  CardHeader: {
    description: "Header section of a Card.",
    props: {}
  },
  // CardTitle
  CardTitle: {
    description: "Title element for Card header.",
    props: {}
  },
  // Checkbox
  Checkbox: {
    description: "Checkbox input for boolean selections.",
    props: {
      size: {
        type: "enum",
        values: ["md", "lg"],
        description: "Size of the checkbox",
        optional: true
      }
    }
  },
  // Command
  Command: {
    description: "Command palette / autocomplete component for searching and selecting.",
    props: {},
    subComponents: ["CommandDialog", "CommandInput", "CommandList", "CommandEmpty", "CommandGroup", "CommandSeparator", "CommandItem", "CommandShortcut"]
  },
  // CommandDialog
  CommandDialog: {
    description: "Command palette in a dialog modal.",
    props: {}
  },
  // CommandEmpty
  CommandEmpty: {
    description: "Content shown when no results are found.",
    props: {}
  },
  // CommandGroup
  CommandGroup: {
    description: "Group of related command items.",
    props: {}
  },
  // CommandInput
  CommandInput: {
    description: "Search input for the command palette.",
    props: {}
  },
  // CommandItem
  CommandItem: {
    description: "Selectable item in the command palette.",
    props: {}
  },
  // CommandList
  CommandList: {
    description: "Container for command items.",
    props: {}
  },
  // CommandSeparator
  CommandSeparator: {
    description: "Visual separator between command groups.",
    props: {}
  },
  // CommandShortcut
  CommandShortcut: {
    description: "Keyboard shortcut indicator for a command item.",
    props: {}
  },
  // DialogContent
  DialogContent: {
    description: "The dialog panel itself.",
    props: {
      size: {
        type: "enum",
        values: ["sm", "md", "lg", "xl", "full"],
        description: "Size variant of the dialog",
        optional: true
      }
    }
  },
  // DialogFooter
  DialogFooter: {
    description: "Footer section for action buttons.",
    props: {}
  },
  // DialogHeader
  DialogHeader: {
    description: "Header section containing title and description.",
    props: {}
  },
  // DropdownMenuContent
  DropdownMenuContent: {
    description: "Content container for dropdown menu items.",
    props: {}
  },
  // DropdownMenuItem
  DropdownMenuItem: {
    description: "Individual item within a dropdown menu.",
    props: {
      variant: {
        type: "enum",
        values: ["default", "danger"],
        description: "Render as destructive/danger action",
        optional: true
      },
      icon: {
        type: "ReactNode",
        description: "Optional icon element",
        optional: true
      }
    }
  },
  // DropdownMenuShortcut
  DropdownMenuShortcut: {
    description: "Keyboard shortcut indicator for a dropdown menu item.",
    props: {}
  },
  // Input
  Input: {
    description: "Text input field with variants for default and error states.",
    props: {
      variant: {
        type: "enum",
        values: ["default", "error"],
        description: "Visual variant of the input",
        optional: true
      },
      size: {
        type: "enum",
        values: ["md", "lg"],
        description: "Size of the input",
        optional: true
      }
    }
  },
  // Label
  Label: {
    description: "Label component for form fields with optional required indicator.",
    props: {
      required: {
        type: "boolean",
        description: "Whether the field is required (shows asterisk)",
        optional: true
      }
    }
  },
  // Pagination
  Pagination: {
    description: "Pagination component for navigating through pages.",
    props: {},
    subComponents: ["PaginationContent", "PaginationItem", "PaginationLink", "PaginationPrevious", "PaginationNext", "PaginationEllipsis"]
  },
  // PaginationContent
  PaginationContent: {
    description: "Container for pagination items.",
    props: {}
  },
  // PaginationEllipsis
  PaginationEllipsis: {
    description: "Ellipsis indicator for skipped page numbers.",
    props: {}
  },
  // PaginationItem
  PaginationItem: {
    description: "Individual item in the pagination.",
    props: {}
  },
  // PaginationLink
  PaginationLink: {
    description: "Link to a specific page.",
    props: {
      isActive: {
        type: "boolean",
        description: "Whether this is the current page",
        optional: true
      },
      asChild: {
        type: "boolean",
        description: "Render as child element (for router links)",
        optional: true
      }
    }
  },
  // PaginationNext
  PaginationNext: {
    description: "Link to the next page.",
    props: {}
  },
  // PaginationPrevious
  PaginationPrevious: {
    description: "Link to the previous page.",
    props: {}
  },
  // PopoverContent
  PopoverContent: {
    description: "The popover panel content.",
    props: {
      showArrow: {
        type: "boolean",
        description: "Show pointing arrow",
        optional: true
      }
    }
  },
  // Progress
  Progress: {
    description: "Progress bar component for displaying completion status.",
    props: {
      value: {
        type: "number",
        description: "Current progress value (0-100)",
        optional: true
      },
      variant: {
        type: "enum",
        values: ["default", "success", "warning", "error"],
        description: "Visual style variant",
        optional: true
      },
      showValue: {
        type: "boolean",
        description: "Show value as label",
        optional: true
      }
    }
  },
  // RadioGroup
  RadioGroup: {
    description: "Group of radio buttons for single selection from multiple options.",
    props: {
      size: {
        type: "enum",
        values: ["md", "lg"],
        description: "Size of the radio items",
        optional: true
      }
    },
    subComponents: ["RadioItem"]
  },
  // RadioItem
  RadioItem: {
    description: "Individual radio button within a RadioGroup.",
    props: {
      size: {
        type: "enum",
        values: ["md", "lg"],
        description: "Size of the radio item (inherited from group if not specified)",
        optional: true
      }
    }
  },
  // Select
  Select: {
    description: "Dropdown select component for choosing from a list of options.",
    props: {
      size: {
        type: "enum",
        values: ["md", "lg"],
        description: "Size of the select trigger",
        optional: true
      },
      placeholder: {
        type: "string",
        description: "Placeholder text when no value selected",
        optional: true
      }
    },
    subComponents: ["SelectTrigger", "SelectValue", "SelectContent", "SelectItem", "SelectGroup", "SelectLabel", "SelectSeparator"]
  },
  // SelectContent
  SelectContent: {
    description: "Content container for Select options.",
    props: {}
  },
  // SelectGroup
  SelectGroup: {
    description: "Group container for related Select items.",
    props: {}
  },
  // SelectItem
  SelectItem: {
    description: "Individual item within a Select.",
    props: {}
  },
  // SelectLabel
  SelectLabel: {
    description: "Label for a SelectGroup.",
    props: {}
  },
  // SelectSeparator
  SelectSeparator: {
    description: "Visual separator between Select items.",
    props: {}
  },
  // SelectTrigger
  SelectTrigger: {
    description: "Trigger button for the Select component.",
    props: {
      size: {
        type: "enum",
        values: ["md", "lg"],
        description: "Size of the trigger",
        optional: true
      },
      placeholder: {
        type: "string",
        description: "Placeholder text",
        optional: true
      }
    }
  },
  // Separator
  Separator: {
    description: "Visual separator for dividing content.",
    props: {
      orientation: {
        type: "enum",
        values: ["horizontal", "vertical"],
        description: "Orientation of the separator",
        optional: true
      },
      decorative: {
        type: "boolean",
        description: "Whether the separator is decorative",
        optional: true
      }
    }
  },
  // Sheet
  Sheet: {
    description: "Slide-out panel from the edge of the screen.",
    props: {},
    subComponents: ["SheetTrigger", "SheetClose", "SheetPortal", "SheetOverlay", "SheetContent", "SheetHeader", "SheetBody", "SheetFooter", "SheetTitle", "SheetDescription"]
  },
  // SheetClose
  SheetClose: {
    description: "Element that closes the sheet.",
    props: {}
  },
  // SheetContent
  SheetContent: {
    description: "The sheet panel itself.",
    props: {
      side: {
        type: "enum",
        values: ["top", "right", "bottom", "left"],
        description: "Side from which the sheet slides in",
        optional: true
      }
    }
  },
  // SheetDescription
  SheetDescription: {
    description: "Description text for the sheet.",
    props: {}
  },
  // SheetFooter
  SheetFooter: {
    description: "Footer section for sheet actions.",
    props: {}
  },
  // SheetHeader
  SheetHeader: {
    description: "Header section of a sheet.",
    props: {}
  },
  // SheetOverlay
  SheetOverlay: {
    description: "Backdrop overlay behind the sheet.",
    props: {}
  },
  // SheetPortal
  SheetPortal: {
    description: "Portal container for sheet content.",
    props: {}
  },
  // SheetTitle
  SheetTitle: {
    description: "Title of the sheet.",
    props: {}
  },
  // SheetTrigger
  SheetTrigger: {
    description: "Element that opens the sheet.",
    props: {}
  },
  // Skeleton
  Skeleton: {
    description: "Skeleton loader for content placeholders.",
    props: {
      width: {
        type: "string",
        description: "Width of skeleton (Tailwind class or arbitrary value)",
        optional: true
      },
      height: {
        type: "string",
        description: "Height of skeleton (Tailwind class or arbitrary value)",
        optional: true
      },
      circle: {
        type: "boolean",
        description: "Whether to render as circle",
        optional: true
      }
    }
  },
  // Spinner
  Spinner: {
    description: "Loading spinner indicator.",
    props: {
      size: {
        type: "enum",
        values: ["sm", "md", "lg"],
        description: "Size of the spinner",
        optional: true
      }
    }
  },
  // Switch
  Switch: {
    description: "Toggle switch for binary on/off states.",
    props: {
      size: {
        type: "enum",
        values: ["md", "lg"],
        description: "Size of the switch",
        optional: true
      }
    }
  },
  // Table
  Table: {
    description: "Data table component for displaying tabular data.",
    props: {},
    subComponents: ["TableHeader", "TableBody", "TableFooter", "TableRow", "TableHead", "TableCell", "TableCaption"]
  },
  // TableBody
  TableBody: {
    description: "Body section of a table (contains TableRow with TableCell cells).",
    props: {}
  },
  // TableCaption
  TableCaption: {
    description: "Table caption for accessibility.",
    props: {}
  },
  // TableCell
  TableCell: {
    description: "Table data cell (td element).",
    props: {}
  },
  // TableFooter
  TableFooter: {
    description: "Footer section of a table.",
    props: {}
  },
  // TableHead
  TableHead: {
    description: "Table header cell (th element).",
    props: {}
  },
  // TableHeader
  TableHeader: {
    description: "Header section of a table (contains TableRow with TableHead cells).",
    props: {}
  },
  // TableRow
  TableRow: {
    description: "Table row element.",
    props: {}
  },
  // TabsContent
  TabsContent: {
    description: "Content panel for a tab.",
    props: {}
  },
  // TabsList
  TabsList: {
    description: "Container for tab triggers.",
    props: {}
  },
  // TabsTrigger
  TabsTrigger: {
    description: "Clickable tab button.",
    props: {
      icon: {
        type: "ReactNode",
        description: "Icon to display before the label",
        optional: true
      }
    }
  },
  // Textarea
  Textarea: {
    description: "Multi-line text input field with variants for default and error states.",
    props: {
      variant: {
        type: "enum",
        values: ["default", "error"],
        description: "Visual variant of the textarea",
        optional: true
      },
      size: {
        type: "enum",
        values: ["md", "lg"],
        description: "Size of the textarea",
        optional: true
      }
    }
  },
  // Toast
  Toast: {
    description: "Toast notification for temporary messages.",
    props: {
      variant: {
        type: "enum",
        values: ["default", "success", "warning", "error"],
        description: "Visual style variant",
        optional: true
      }
    },
    subComponents: ["ToastProvider", "ToastViewport", "ToastTitle", "ToastDescription", "ToastAction", "ToastClose"]
  },
  // ToastAction
  ToastAction: {
    description: "Interactive action button for a toast.",
    props: {}
  },
  // Toaster
  Toaster: {
    description: "Container component that renders all active toasts. Place once at root of your app.",
    props: {
      placement: {
        type: "enum",
        values: ["top-left", "top-center", "top-right", "bottom-left", "bottom-center", "bottom-right"],
        default: "bottom-right",
        description: "Position on screen",
        optional: true
      }
    }
  },
  // ToastViewport
  ToastViewport: {
    description: "Container where toasts are rendered.",
    props: {}
  },
  // TooltipContent
  TooltipContent: {
    description: "The tooltip popup content.",
    props: {
      showArrow: {
        type: "boolean",
        description: "Arrow visibility",
        optional: true
      }
    }
  }
};
var availableComponents = [
  "Accordion",
  "AccordionContent",
  "AccordionItem",
  "AccordionTrigger",
  "Alert",
  "AlertDescription",
  "AlertTitle",
  "Avatar",
  "AvatarFallback",
  "AvatarImage",
  "Badge",
  "Breadcrumb",
  "BreadcrumbEllipsis",
  "BreadcrumbItem",
  "BreadcrumbLink",
  "BreadcrumbList",
  "BreadcrumbPage",
  "BreadcrumbSeparator",
  "Button",
  "Card",
  "CardContent",
  "CardDescription",
  "CardFooter",
  "CardHeader",
  "CardTitle",
  "Checkbox",
  "Command",
  "CommandDialog",
  "CommandEmpty",
  "CommandGroup",
  "CommandInput",
  "CommandItem",
  "CommandList",
  "CommandSeparator",
  "CommandShortcut",
  "DialogContent",
  "DialogFooter",
  "DialogHeader",
  "DropdownMenuContent",
  "DropdownMenuItem",
  "DropdownMenuShortcut",
  "Input",
  "Label",
  "Pagination",
  "PaginationContent",
  "PaginationEllipsis",
  "PaginationItem",
  "PaginationLink",
  "PaginationNext",
  "PaginationPrevious",
  "PopoverContent",
  "Progress",
  "RadioGroup",
  "RadioItem",
  "Select",
  "SelectContent",
  "SelectGroup",
  "SelectItem",
  "SelectLabel",
  "SelectSeparator",
  "SelectTrigger",
  "Separator",
  "Sheet",
  "SheetClose",
  "SheetContent",
  "SheetDescription",
  "SheetFooter",
  "SheetHeader",
  "SheetOverlay",
  "SheetPortal",
  "SheetTitle",
  "SheetTrigger",
  "Skeleton",
  "Spinner",
  "Switch",
  "Table",
  "TableBody",
  "TableCaption",
  "TableCell",
  "TableFooter",
  "TableHead",
  "TableHeader",
  "TableRow",
  "TabsContent",
  "TabsList",
  "TabsTrigger",
  "Textarea",
  "Toast",
  "ToastAction",
  "Toaster",
  "ToastViewport",
  "TooltipContent"
];

// src/accordion/Accordion.tsx
import * as React26 from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";

// src/accordion/constants.ts
var itemStyles4 = "border-b border-[#E5E5E5] dark:border-[#333333]";
var triggerStyles2 = [
  "flex flex-1 items-center justify-between",
  "py-4 w-full",
  "font-sans font-light",
  "text-[14px] lg:text-[18px]",
  "text-[#171717] dark:text-white",
  "text-left",
  "transition-colors duration-200 ease-out",
  "hover:text-[#3941EB] dark:hover:text-[#00FBA9]",
  "[&[data-state=open]>svg]:rotate-180"
].join(" ");
var iconStyles2 = [
  "size-5 shrink-0",
  "text-[#A9A9A9]",
  "transition-transform duration-200 ease-out"
].join(" ");
var contentStyles8 = [
  "overflow-hidden",
  "data-[state=closed]:animate-accordion-up",
  "data-[state=open]:animate-accordion-down"
].join(" ");
var contentInnerStyles = [
  "pb-4",
  "font-sans font-light",
  "text-[14px] lg:text-[18px]",
  "text-[#A9A9A9]"
].join(" ");

// src/accordion/Accordion.tsx
import { jsx as jsx26, jsxs as jsxs17 } from "react/jsx-runtime";
var Accordion = AccordionPrimitive.Root;
var AccordionItem = React26.forwardRef(({ ...props }, ref) => /* @__PURE__ */ jsx26(AccordionPrimitive.Item, { ref, className: itemStyles4, ...props }));
AccordionItem.displayName = "AccordionItem";
var AccordionTrigger = React26.forwardRef(({ children, ...props }, ref) => /* @__PURE__ */ jsx26(AccordionPrimitive.Header, { className: "flex", children: /* @__PURE__ */ jsxs17(AccordionPrimitive.Trigger, { ref, className: triggerStyles2, ...props, children: [
  children,
  /* @__PURE__ */ jsx26(D, { strokeWidth: 1, className: iconStyles2 })
] }) }));
AccordionTrigger.displayName = "AccordionTrigger";
var AccordionContent = React26.forwardRef(({ children, ...props }, ref) => /* @__PURE__ */ jsx26(AccordionPrimitive.Content, { ref, className: contentStyles8, ...props, children: /* @__PURE__ */ jsx26("div", { className: contentInnerStyles, children }) }));
AccordionContent.displayName = "AccordionContent";

// src/sheet/Sheet.tsx
import * as React27 from "react";
import * as DialogPrimitive2 from "@radix-ui/react-dialog";

// src/sheet/constants.ts
var overlayStyles2 = [
  "fixed inset-0 z-50",
  "bg-black/50",
  "data-[state=open]:animate-in data-[state=closed]:animate-out",
  "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
].join(" ");
var contentBaseStyles2 = [
  "fixed z-50",
  "bg-white dark:bg-[#171717]",
  "border-[#E5E5E5] dark:border-[#333333]",
  "shadow-lg",
  "flex flex-col",
  "transition-transform duration-200 ease-out",
  "data-[state=open]:animate-in data-[state=closed]:animate-out"
].join(" ");
var sideStyles = {
  top: [
    "inset-x-0 top-0",
    "border-b",
    "data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top"
  ].join(" "),
  right: [
    "inset-y-0 right-0 h-full w-full sm:max-w-sm",
    "border-l",
    "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right"
  ].join(" "),
  bottom: [
    "inset-x-0 bottom-0",
    "border-t",
    "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom"
  ].join(" "),
  left: [
    "inset-y-0 left-0 h-full w-full sm:max-w-sm",
    "border-r",
    "data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left"
  ].join(" ")
};
var closeButtonStyles3 = [
  "absolute right-4 top-4",
  "p-1",
  "text-[#A9A9A9]",
  "transition-colors duration-200 ease-out",
  "hover:text-[#171717] dark:hover:text-white",
  "focus:outline-none"
].join(" ");
var headerStyles3 = [
  "flex flex-col gap-2",
  "p-6"
].join(" ");
var footerStyles3 = [
  "flex flex-col-reverse sm:flex-row sm:justify-end gap-2",
  "p-6 pt-0"
].join(" ");
var titleStyles5 = [
  "font-sans font-light",
  "text-[23px] lg:text-[29px]",
  "leading-[1.1]",
  "text-[#171717] dark:text-white"
].join(" ");
var descriptionStyles5 = [
  "font-sans font-light",
  "text-[14px] lg:text-[18px]",
  "text-[#A9A9A9]"
].join(" ");
var bodyStyles = [
  "flex-1 overflow-auto",
  "p-6 pt-0"
].join(" ");

// src/sheet/Sheet.tsx
import { jsx as jsx27, jsxs as jsxs18 } from "react/jsx-runtime";
var Sheet = DialogPrimitive2.Root;
var SheetTrigger = DialogPrimitive2.Trigger;
var SheetClose = DialogPrimitive2.Close;
var SheetPortal = DialogPrimitive2.Portal;
var SheetOverlay = React27.forwardRef((props, ref) => /* @__PURE__ */ jsx27(DialogPrimitive2.Overlay, { ref, className: overlayStyles2, ...props }));
SheetOverlay.displayName = "SheetOverlay";
var SheetContent = React27.forwardRef(({ side = "right", children, ...props }, ref) => /* @__PURE__ */ jsxs18(SheetPortal, { children: [
  /* @__PURE__ */ jsx27(SheetOverlay, {}),
  /* @__PURE__ */ jsxs18(
    DialogPrimitive2.Content,
    {
      ref,
      className: `${contentBaseStyles2} ${sideStyles[side]}`,
      ...props,
      children: [
        /* @__PURE__ */ jsxs18(DialogPrimitive2.Close, { className: closeButtonStyles3, children: [
          /* @__PURE__ */ jsx27(R, { strokeWidth: 1, className: "size-5" }),
          /* @__PURE__ */ jsx27("span", { className: "sr-only", children: "Close" })
        ] }),
        children
      ]
    }
  )
] }));
SheetContent.displayName = "SheetContent";
var SheetHeader = React27.forwardRef(
  (props, ref) => /* @__PURE__ */ jsx27("div", { ref, className: headerStyles3, ...props })
);
SheetHeader.displayName = "SheetHeader";
var SheetFooter = React27.forwardRef(
  (props, ref) => /* @__PURE__ */ jsx27("div", { ref, className: footerStyles3, ...props })
);
SheetFooter.displayName = "SheetFooter";
var SheetTitle = React27.forwardRef((props, ref) => /* @__PURE__ */ jsx27(DialogPrimitive2.Title, { ref, className: titleStyles5, ...props }));
SheetTitle.displayName = "SheetTitle";
var SheetDescription = React27.forwardRef((props, ref) => /* @__PURE__ */ jsx27(DialogPrimitive2.Description, { ref, className: descriptionStyles5, ...props }));
SheetDescription.displayName = "SheetDescription";
var SheetBody = React27.forwardRef(
  (props, ref) => /* @__PURE__ */ jsx27("div", { ref, className: bodyStyles, ...props })
);
SheetBody.displayName = "SheetBody";

// src/table/Table.tsx
import * as React28 from "react";

// src/table/constants.ts
var wrapperStyles = "relative w-full overflow-auto";
var tableStyles = "w-full caption-bottom text-[14px] lg:text-[18px]";
var headerStyles4 = "[&_tr]:border-b";
var bodyStyles2 = "[&_tr:last-child]:border-0";
var footerStyles4 = [
  "border-t border-[#E5E5E5] dark:border-[#333333]",
  "bg-[#FAFAFA] dark:bg-[#171717]",
  "font-sans font-light"
].join(" ");
var rowStyles = [
  "border-b border-[#E5E5E5] dark:border-[#333333]",
  "transition-colors duration-200 ease-out",
  "hover:bg-[#FAFAFA] dark:hover:bg-white/5",
  "data-[state=selected]:bg-[#FAFAFA] dark:data-[state=selected]:bg-white/5"
].join(" ");
var headStyles = [
  "h-12 px-4",
  "text-left align-middle",
  "font-mono font-extralight uppercase",
  "text-[14px]",
  "text-[#A9A9A9]",
  "[&:has([role=checkbox])]:pr-0"
].join(" ");
var cellStyles = [
  "p-4",
  "align-middle",
  "font-sans font-light",
  "text-[#171717] dark:text-white",
  "[&:has([role=checkbox])]:pr-0"
].join(" ");
var captionStyles = [
  "mt-4",
  "font-mono font-extralight uppercase",
  "text-[14px]",
  "text-[#A9A9A9]"
].join(" ");

// src/table/Table.tsx
import { jsx as jsx28 } from "react/jsx-runtime";
var Table = React28.forwardRef((props, ref) => /* @__PURE__ */ jsx28("div", { className: wrapperStyles, children: /* @__PURE__ */ jsx28("table", { ref, className: tableStyles, ...props }) }));
Table.displayName = "Table";
var TableHeader = React28.forwardRef(
  (props, ref) => /* @__PURE__ */ jsx28("thead", { ref, className: headerStyles4, ...props })
);
TableHeader.displayName = "TableHeader";
var TableBody = React28.forwardRef(
  (props, ref) => /* @__PURE__ */ jsx28("tbody", { ref, className: bodyStyles2, ...props })
);
TableBody.displayName = "TableBody";
var TableFooter = React28.forwardRef(
  (props, ref) => /* @__PURE__ */ jsx28("tfoot", { ref, className: footerStyles4, ...props })
);
TableFooter.displayName = "TableFooter";
var TableRow = React28.forwardRef(
  (props, ref) => /* @__PURE__ */ jsx28("tr", { ref, className: rowStyles, ...props })
);
TableRow.displayName = "TableRow";
var TableHead = React28.forwardRef(
  (props, ref) => /* @__PURE__ */ jsx28("th", { ref, className: headStyles, ...props })
);
TableHead.displayName = "TableHead";
var TableCell = React28.forwardRef(
  (props, ref) => /* @__PURE__ */ jsx28("td", { ref, className: cellStyles, ...props })
);
TableCell.displayName = "TableCell";
var TableCaption = React28.forwardRef(
  (props, ref) => /* @__PURE__ */ jsx28("caption", { ref, className: captionStyles, ...props })
);
TableCaption.displayName = "TableCaption";

// src/command/Command.tsx
import * as React29 from "react";
import { Command as CommandPrimitive } from "cmdk";
import * as DialogPrimitive3 from "@radix-ui/react-dialog";

// src/command/constants.ts
var commandStyles = [
  "flex flex-col h-full w-full",
  "overflow-hidden",
  "bg-white dark:bg-[#171717]"
].join(" ");
var dialogContentStyles = [
  "overflow-hidden p-0",
  "[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-mono [&_[cmdk-group-heading]]:font-extralight",
  "[&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:text-[14px] [&_[cmdk-group-heading]]:text-[#A9A9A9]",
  "[&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0",
  "[&_[cmdk-input-wrapper]_svg]:size-5",
  "[&_[cmdk-input]]:h-12",
  "[&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3",
  "[&_[cmdk-item]_svg]:size-5"
].join(" ");
var inputWrapperStyles = [
  "flex items-center",
  "border-b border-[#E5E5E5] dark:border-[#333333]",
  "px-3"
].join(" ");
var inputStyles = [
  "flex h-11 w-full",
  "bg-transparent",
  "py-3",
  "font-sans font-light",
  "text-[14px] lg:text-[18px]",
  "text-[#171717] dark:text-white",
  "placeholder:text-[#A9A9A9]",
  "outline-none",
  "disabled:cursor-not-allowed disabled:opacity-50"
].join(" ");
var listStyles3 = [
  "max-h-[300px]",
  "overflow-y-auto overflow-x-hidden"
].join(" ");
var emptyStyles = [
  "py-6",
  "text-center",
  "font-sans font-light",
  "text-[14px] lg:text-[18px]",
  "text-[#A9A9A9]"
].join(" ");
var groupStyles2 = [
  "overflow-hidden p-1",
  "text-[#171717] dark:text-white",
  "[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5",
  "[&_[cmdk-group-heading]]:font-mono [&_[cmdk-group-heading]]:font-extralight",
  "[&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:text-[14px]",
  "[&_[cmdk-group-heading]]:text-[#A9A9A9]"
].join(" ");
var separatorStyles4 = "-mx-1 h-px bg-[#E5E5E5] dark:bg-[#333333]";
var itemStyles5 = [
  "relative flex cursor-pointer select-none items-center gap-2",
  "px-2 py-1.5",
  "font-sans font-light",
  "text-[14px] lg:text-[18px]",
  "outline-none",
  "transition-colors duration-200 ease-out",
  "data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50",
  "data-[selected=true]:bg-[#FAFAFA] dark:data-[selected=true]:bg-white/5",
  "data-[selected=true]:text-[#171717] dark:data-[selected=true]:text-white"
].join(" ");
var shortcutStyles2 = [
  "ml-auto",
  "font-mono font-extralight uppercase",
  "text-[14px]",
  "text-[#A9A9A9]",
  "tracking-wide"
].join(" ");

// src/command/Command.tsx
import { jsx as jsx29, jsxs as jsxs19 } from "react/jsx-runtime";
var Command = React29.forwardRef((props, ref) => /* @__PURE__ */ jsx29(CommandPrimitive, { ref, className: commandStyles, ...props }));
Command.displayName = "Command";
var CommandDialog = ({ children, ...props }) => /* @__PURE__ */ jsx29(DialogPrimitive3.Root, { ...props, children: /* @__PURE__ */ jsxs19(DialogPrimitive3.Portal, { children: [
  /* @__PURE__ */ jsx29(DialogPrimitive3.Overlay, { className: "fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" }),
  /* @__PURE__ */ jsx29(DialogPrimitive3.Content, { className: `fixed left-[50%] top-[50%] z-50 w-full max-w-lg translate-x-[-50%] translate-y-[-50%] bg-white dark:bg-[#171717] border border-[#E5E5E5] dark:border-[#333333] shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] duration-200 ${dialogContentStyles}`, children: /* @__PURE__ */ jsx29(Command, { children }) })
] }) });
CommandDialog.displayName = "CommandDialog";
var CommandInput = React29.forwardRef((props, ref) => /* @__PURE__ */ jsxs19("div", { className: inputWrapperStyles, "cmdk-input-wrapper": "", children: [
  /* @__PURE__ */ jsx29(M, { strokeWidth: 1, className: "mr-2 size-5 shrink-0 text-[#A9A9A9]" }),
  /* @__PURE__ */ jsx29(CommandPrimitive.Input, { ref, className: inputStyles, ...props })
] }));
CommandInput.displayName = "CommandInput";
var CommandList = React29.forwardRef((props, ref) => /* @__PURE__ */ jsx29(CommandPrimitive.List, { ref, className: listStyles3, ...props }));
CommandList.displayName = "CommandList";
var CommandEmpty = React29.forwardRef((props, ref) => /* @__PURE__ */ jsx29(CommandPrimitive.Empty, { ref, className: emptyStyles, ...props }));
CommandEmpty.displayName = "CommandEmpty";
var CommandGroup = React29.forwardRef((props, ref) => /* @__PURE__ */ jsx29(CommandPrimitive.Group, { ref, className: groupStyles2, ...props }));
CommandGroup.displayName = "CommandGroup";
var CommandSeparator = React29.forwardRef((props, ref) => /* @__PURE__ */ jsx29(CommandPrimitive.Separator, { ref, className: separatorStyles4, ...props }));
CommandSeparator.displayName = "CommandSeparator";
var CommandItem = React29.forwardRef((props, ref) => /* @__PURE__ */ jsx29(CommandPrimitive.Item, { ref, className: itemStyles5, ...props }));
CommandItem.displayName = "CommandItem";
var CommandShortcut = React29.forwardRef(
  (props, ref) => /* @__PURE__ */ jsx29("span", { ref, className: shortcutStyles2, ...props })
);
CommandShortcut.displayName = "CommandShortcut";
export {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Alert,
  AlertDescription,
  AlertTitle,
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Checkbox,
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  Input,
  Label,
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  Popover,
  PopoverAnchor,
  PopoverArrow,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
  Progress,
  RadioGroup,
  RadioItem,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  Separator3 as Separator,
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
  Skeleton,
  Spinner,
  Switch,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  Toast,
  ToastAction,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
  Toaster,
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  availableComponents,
  componentMeta,
  dismissAll,
  dismissToast,
  toast,
  useToast
};
